import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';

const fallbackMotion = {
  killTweensOf() {},
  to(target, vars = {}) {
    if (target) {
      ['x', 'y', 'z'].forEach(key => {
        if (!(key in vars)) return;
        const value = vars[key];
        if (typeof value === 'number') {
          target[key] = value;
          return;
        }
        const relative = typeof value === 'string' ? value.match(/^([+-])=(\d+(?:\.\d+)?)$/) : null;
        if (relative) {
          const delta = Number.parseFloat(relative[2]) * (relative[1] === '-' ? -1 : 1);
          target[key] = (Number(target[key]) || 0) + delta;
        }
      });
    }
    vars.onUpdate?.();
    vars.onComplete?.();
    return { kill() {} };
  }
};

const getMotion = () => window.gsap || fallbackMotion;

class BoardCustomizer extends HTMLElement {
  constructor() {
    super();
    this.decks = this.enrichFilterItems(this.parseJsonAttribute('data-decks'), 'deck');
    this.grips = this.parseJsonAttribute('data-grips');
    this.wheels = this.enrichFilterItems(this.parseJsonAttribute('data-wheels'), 'wheel');
    this.bearings = this.parseJsonAttribute('data-bearings');
    this.trucks = this.enrichFilterItems(this.parseJsonAttribute('data-trucks'), 'truck');
    this.hardware = this.parseJsonAttribute('data-hardware');
    this.bolts = this.parseJsonAttribute('data-bolts');
    if (!this.hardware.length) this.hardware = this.bolts;
    this.metals = this.parseJsonAttribute('data-metals');
    this.modelUrl = this.getAttribute('data-model-url');
    this.rootUrl = this.getAttribute('data-root-url') || '/';
    this.fallbackCurrency = this.getAttribute('data-fallback-currency') || 'SGD';
    this.defaultDeckUrl = this.getAttribute('data-default-deck');
    this.defaultGripUrl = this.getAttribute('data-default-grip');
    this.defaultWheelUrl = this.getAttribute('data-default-wheel');
    this.defaultGripColor = this.getAttribute('data-default-grip-color');
    this.defaultTruckColor = this.getAttribute('data-default-truck-color');
    this.defaultBearingColor = this.getAttribute('data-default-bearing-color');
    this.defaultHardwareColor = this.getAttribute('data-default-hardware-color');
    this.defaultBoltColor = this.getAttribute('data-default-bolt-color');
    this.productVariantId = this.getAttribute('data-product-variant-id');
    this.selectedDeck = null;
    this.selectedGrip = null;
    this.selectedWheel = null;
    this.selectedBearing = null;
    this.selectedTruck = null;
    this.selectedHardware = null;
    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.controls = null;
    this.textureLoader = new THREE.TextureLoader();
    this.deckMesh = null;
    this.gripTapeMesh = null;
    this.boltsMesh = null;
    this.baseplatesMesh = null;
    this.bearingRefs = [];
    this.truck1Mesh = null;
    this.truck2Mesh = null;
    this.wheelRefs = [];
    this.deckMaterial = null;
    this.gripMaterial = null;
    this.wheelMaterial = null;
    this.bearingMaterial = null;
    this.truckHangerMaterial = null;
    this.truckBaseplateMaterial = null;
    this.truckBushingTopMaterial = null;
    this.truckBushingBottomMaterial = null;
    this.truckWasherTopMaterial = null;
    this.truckWasherBottomMaterial = null;
    this.boltMaterial = null;
    this.axleNutMaterial = null;
    this.kingpinNutMaterial = null;
    this.bearingCapGeometry = null;
    this.defaultCameraPosition = new THREE.Vector3(3.375, 1.425, 0);
    this.defaultCameraTarget = new THREE.Vector3(0, 0.3, 0);
    this.textureCache = {};
    this.texturePromises = {};
    this.deckTextureRequestId = 0;
    this.gripTextureRequestId = 0;
    this.wheelTextureRequestId = 0;
    THREE.Cache.enabled = true;
    this.scrollLockY = 0;
    this.scrollLockCount = 0;
    this.scrollLockUsesFixedBody = false;
    this.lastFocusedElement = null;
    this.pendingCartRemovals = new Set();
    this.cartChangeQueue = Promise.resolve();
    this.cartActiveChanges = 0;
    this.cartLoadRequestId = 0;
    this.cartRefreshAfterChangeId = 0;
    this.cartDrawerItemOrder = [];
    this.cartDrawerCloseTimer = 0;
    this.cartDrawerSuppressCloseUntil = 0;
    this.cartDrawerSuppressOpenUntil = 0;
    this.cartDrawerIsOpenOrOpening = false;
    this.cartDrawerScrollLocked = false;
    this.cartViewportListenersBound = false;
    this.cartClearReturnFocus = null;
    this.cartClearRenderToken = 0;
    this.cartLastFetchedAt = 0;
    this.latestCart = null;
    this.isAddingToCart = false;
    this.activeCanvasPointerId = null;
    this.activeCanvasPointerIds = new Set();
    this.isCanvasDragging = false;
    this.isForwardingCanvasPointerEnd = false;
    this.isReleasingCanvasDrag = false;
    this.activeDrawerFilters = {};
    this.activeDrawerSort = {};
    this.imageModalRequestId = 0;
    this.loadedDrawerImages = new Set();
    this.imagePreloadPromises = new Map();
    this.drawerImageObservers = new Map();
    this.imageModalLastFocusedElement = null;
    this.imageModalImages = [];
    this.imageModalIndex = 0;
    this.imageModalSwipe = null;
    this.menuLastFocusedElement = null;
    this.hoveredProductCard = null;
    this.animationFrameId = null;
    this.canvasResizeObserver = null;
    this.handleWindowResize = () => this.handleResize();
    this.handleCartViewportResize = () => this.syncCartDrawerViewportHeight();
  }
  parseJsonAttribute(attributeName) {
    const rawValue = this.getAttribute(attributeName) || '[]';
    const textarea = document.createElement('textarea');
    textarea.innerHTML = rawValue;
    try {
      return JSON.parse(textarea.value);
    } catch (error) {
      console.error(`Board customizer could not parse ${attributeName}:`, error);
      return [];
    }
  }
  normalizeTagList(tags) {
    if (Array.isArray(tags)) return tags.map(tag => String(tag).trim()).filter(Boolean);
    if (!tags) return [];
    return String(tags).split('|').map(tag => tag.trim()).filter(Boolean);
  }
  getBuilderTagValue(tags, key) {
    const prefix = `${key}:`;
    const match = this.normalizeTagList(tags).find(tag => tag.toLowerCase().startsWith(prefix));
    return match ? match.slice(prefix.length).trim() : '';
  }
  normalizeWidthValue(value) {
    const match = String(value || '').replace(',', '.').match(/(\d{1,2}(?:\.\d{1,3})?)/);
    if (!match) return '';
    const width = Number.parseFloat(match[1]);
    if (!Number.isFinite(width) || width < 6 || width > 12) return '';
    const label = width.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
    return label.includes('.') ? label : `${label}.0`;
  }
  normalizeDeckFitRange(value) {
    if (Array.isArray(value)) return this.normalizeDeckFitRange(value.join('-'));
    const text = String(value || '').replace(/[–—]/g, '-').replace(/\bto\b/gi, '-');
    const widths = (text.match(/\d{1,2}(?:[.,]\d{1,3})?/g) || [])
      .map(part => this.normalizeWidthValue(part))
      .filter(Boolean);
    if (!widths.length) return '';
    if (widths.length === 1) return widths[0];
    const range = widths.slice(0, 2).map(part => Number.parseFloat(part)).sort((a, b) => a - b);
    return `${this.normalizeWidthValue(range[0])}-${this.normalizeWidthValue(range[1])}`;
  }
  parseDeckFitRange(value) {
    const label = this.normalizeDeckFitRange(value);
    if (!label) return null;
    const parts = label.split('-').map(part => Number.parseFloat(part)).filter(Number.isFinite);
    if (!parts.length) return null;
    const min = Math.min(parts[0], parts[1] ?? parts[0]);
    const max = Math.max(parts[0], parts[1] ?? parts[0]);
    return { min, max, label };
  }
  extractWidthFromName(name, requireDimension = false) {
    const text = String(name || '');
    const dimensionMatch = text.match(/(\d{1,2}(?:\.\d{1,3})?)\s*(?:"|in|inch|”)?\s*[x×]\s*\d/i);
    if (dimensionMatch) return this.normalizeWidthValue(dimensionMatch[1]);
    if (requireDimension) return '';
    const looseMatch = text.match(/(?:^|[\s(/-])(\d{1,2}(?:\.\d{1,3})?)\s*(?:"|in|inch|”)?(?:[\s)/-]|$)/i);
    return looseMatch ? this.normalizeWidthValue(looseMatch[1]) : '';
  }
  extractTruckWidthFromName(name) {
    const text = String(name || '');
    const parts = text.split(/\s+-\s+/).map(part => part.trim()).filter(Boolean);
    if (parts.length > 1) {
      const variantWidth = this.extractWidthFromName(parts[parts.length - 1], false);
      if (variantWidth) return variantWidth;
    }
    const parsedWidth = this.extractWidthFromName(name, false);
    if (parsedWidth) return parsedWidth;
    const lowerText = text.toLowerCase();
    const independentMatch = lowerText.match(/\bindependent\b[\s\S]*\b(139|144)\b|\b(139|144)\b[\s\S]*\bindependent\b/);
    const independentSize = independentMatch ? (independentMatch[1] || independentMatch[2]) : '';
    if (independentSize === '139') return '8.0';
    if (independentSize === '144') return '8.25';
    return '';
  }
  escapeRegExp(text) {
    return String(text || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
  getVariantTitle(item) {
    const title = String(item?.variantTitle || '').trim();
    return title && title !== 'Default Title' ? title : '';
  }
  getVariantSizeCandidate(item = {}) {
    const variantTitle = this.getVariantTitle(item);
    const sourceText = [
      variantTitle,
      Array.isArray(item?.variantOptions) ? item.variantOptions.join(' ') : '',
      item?.selectedSize || ''
    ].filter(Boolean).join(' ');
    const cleanedText = sourceText
      .replace(/\+\s*\$?\d+(?:\.\d{1,2})?/g, ' ')
      .replace(/\b(?:no|without|add|with)\s+grip(?:\s*tape)?\b/gi, ' ')
      .replace(/\bgriptape\b|\bgrip\s*tape\b|\bdeck\s*only\b/gi, ' ')
      .replace(/\bdefault\s+title\b/gi, ' ')
      .replace(/\s{2,}/g, ' ')
      .trim();
    const width = this.normalizeWidthValue(cleanedText) || this.extractWidthFromName(cleanedText, false);
    const rawMatch = cleanedText.match(/\d{1,3}(?:[.,]\d{1,3})?/);
    const raw = rawMatch ? rawMatch[0].replace(',', '.') : '';
    return {
      raw,
      width,
      label: width ? this.formatSpecValue('width', width) : raw
    };
  }
  getVariantDimensionLabel(item = {}) {
    const sourceText = [
      this.getVariantTitle(item),
      Array.isArray(item?.variantOptions) ? item.variantOptions.join(' ') : '',
      item?.selectedSize || ''
    ].filter(Boolean).join(' ');
    const cleanedText = sourceText
      .replace(/\+\s*\$?\d+(?:\.\d{1,2})?/g, ' ')
      .replace(/\b(?:no|without|add|with)\s+grip(?:\s*tape)?\b/gi, ' ')
      .replace(/\bgriptape\b|\bgrip\s*tape\b|\bdeck\s*only\b/gi, ' ')
      .replace(/\bdefault\s+title\b/gi, ' ')
      .replace(/\s{2,}/g, ' ')
      .trim();
    const match = cleanedText.match(/\d{1,2}(?:[.,]\d{1,3})?\s*(?:"|”|in|inch)?\s*[x×]\s*\d{1,2}(?:[.,]\d{1,3})?\s*(?:"|”|in|inch)?/i);
    return match ? match[0].replace(/\s+/g, ' ').trim() : '';
  }
  hasVariantSizeGroup(title) {
    const text = String(title || '');
    return (
      /\d{1,2}(?:[.,]\d{1,3})?\s*(?:"|”|in|inch)?\s*[x×]\s*\d{1,2}(?:[.,]\d{1,3})?\s*(?:"|”|in|inch)?(?:\s*\/\s*\d{1,2}(?:[.,]\d{1,3})?\s*(?:"|”|in|inch)?\s*[x×]\s*\d{1,2}(?:[.,]\d{1,3})?\s*(?:"|”|in|inch)?)+/i.test(text) ||
      /\b\d{1,3}(?:[.,]\d{1,3})?\s*(?:"|”|in|inch)?(?:\s*\/\s*\d{1,3}(?:[.,]\d{1,3})?\s*(?:"|”|in|inch)?)+/i.test(text)
    );
  }
  insertDeckDimension(title, dimensionLabel) {
    const baseTitle = String(title || '').trim();
    const dimension = String(dimensionLabel || '').trim();
    if (!baseTitle || !dimension) return baseTitle;
    if (baseTitle.toLowerCase().includes(dimension.toLowerCase())) return baseTitle;
    if (/\bskateboard\s+deck\b/i.test(baseTitle)) {
      return baseTitle.replace(/\bskateboard\s+deck\b/i, `${dimension} Skateboard Deck`);
    }
    if (/\bdeck\b/i.test(baseTitle)) {
      return baseTitle.replace(/\bdeck\b/i, `${dimension} Deck`);
    }
    return `${baseTitle} - ${dimension}`;
  }
  normalizeNumericToken(value) {
    const match = String(value || '').replace(',', '.').match(/\d{1,3}(?:\.\d{1,3})?/);
    if (!match) return '';
    const number = Number.parseFloat(match[0]);
    if (!Number.isFinite(number)) return '';
    return number.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
  }
  chooseVariantSegment(segments, sizeCandidate) {
    const wanted = this.normalizeNumericToken(sizeCandidate.width || sizeCandidate.raw || sizeCandidate.label);
    if (!wanted) return '';
    return segments.find(segment => {
      const segmentNumber = this.normalizeNumericToken(segment);
      return segmentNumber && segmentNumber === wanted;
    }) || '';
  }
  chooseTruckVariantSegment(segments, sizeCandidate) {
    const exact = this.chooseVariantSegment(segments, sizeCandidate);
    if (exact) return exact;
    const wanted = Number.parseFloat(this.normalizeNumericToken(sizeCandidate.width || sizeCandidate.raw || sizeCandidate.label));
    if (!Number.isFinite(wanted) || segments.length !== 2) return '';
    const segmentNumbers = segments
      .map(segment => Number.parseFloat(this.normalizeNumericToken(segment)))
      .filter(Number.isFinite);
    if (segmentNumbers.length !== 2) return '';
    const maxSegment = Math.max(...segmentNumbers);
    if (maxSegment <= 6 || maxSegment >= 100) {
      return wanted <= 8 ? segments[0] : segments[1];
    }
    return '';
  }
  getVariantSpecificTitle(productTitle, item = {}) {
    const title = String(productTitle || '').trim();
    if (!title) return '';
    const sizeCandidate = this.getVariantSizeCandidate(item);
    if (!sizeCandidate.raw && !sizeCandidate.width) return title;
    const chooseSegment = segments => (
      item?.type === 'truck'
        ? this.chooseTruckVariantSegment(segments, sizeCandidate)
        : this.chooseVariantSegment(segments, sizeCandidate)
    );
    let nextTitle = title;
    nextTitle = nextTitle.replace(
      /\d{1,2}(?:[.,]\d{1,3})?\s*(?:"|”|in|inch)?\s*[x×]\s*\d{1,2}(?:[.,]\d{1,3})?\s*(?:"|”|in|inch)?(?:\s*\/\s*\d{1,2}(?:[.,]\d{1,3})?\s*(?:"|”|in|inch)?\s*[x×]\s*\d{1,2}(?:[.,]\d{1,3})?\s*(?:"|”|in|inch)?)+/gi,
      match => {
        const chosen = chooseSegment(match.split(/\s*\/\s*/));
        return chosen || match;
      }
    );
    nextTitle = nextTitle.replace(
      /\b\d{1,3}(?:[.,]\d{1,3})?\s*(?:"|”|in|inch)?(?:\s*\/\s*\d{1,3}(?:[.,]\d{1,3})?\s*(?:"|”|in|inch)?)+/gi,
      match => {
        const chosen = chooseSegment(match.split(/\s*\/\s*/));
        return chosen || match;
      }
    );
    return nextTitle.replace(/\s{2,}/g, ' ').trim();
  }
  stripVariantMeasurementsFromTitle(title, variantTitle) {
    let base = String(title || '').trim();
    if (!base) return '';
    base = base
      .replace(/\s+\d{1,2}(?:\.\d{1,3})?\s*(?:"|in|inch|”)?\s*[x×]\s*\d{1,2}(?:\.\d{1,3})?\s*(?:"|in|inch|”)?(?:\s*\/\s*\d{1,2}(?:\.\d{1,3})?\s*(?:"|in|inch|”)?\s*[x×]\s*\d{1,2}(?:\.\d{1,3})?\s*(?:"|in|inch|”)?)+/gi, ' ')
      .replace(/\s+\d{1,2}(?:\.\d{1,3})?\s*(?:"|in|inch|”)?\s*[x×]\s*\d{1,2}(?:\.\d{1,3})?\s*(?:"|in|inch|”)?/gi, ' ');
    const width = this.normalizeWidthValue(variantTitle);
    if (width) {
      const widthPattern = this.escapeRegExp(width).replace(/\\\.0$/, '(?:\\.0)?');
      base = base.replace(new RegExp(`\\s+${widthPattern}\\s*(?:"|in|inch|”)?(?=\\s*(?:skateboard\\s+deck|deck|$))`, 'i'), ' ');
    }
    return base.replace(/\s{2,}/g, ' ').trim();
  }
  getItemDisplayName(item) {
    if (!item) return '';
    const variantTitle = this.getVariantTitle(item);
    if (!variantTitle) return item.displayName || item.name || '';
    const productTitle = item.productTitle || String(item.name || '').split(/\s+-\s+/)[0] || '';
    if (productTitle && !this.hasVariantSizeGroup(productTitle)) return productTitle;
    const variantSpecificTitle = this.getVariantSpecificTitle(productTitle, item);
    if (variantSpecificTitle && variantSpecificTitle !== productTitle) return variantSpecificTitle;
    if (item?.type === 'truck' && productTitle && this.hasVariantSizeGroup(productTitle)) return productTitle;
    const dimensionLabel = this.getVariantDimensionLabel(item);
    if (dimensionLabel) return this.insertDeckDimension(productTitle, dimensionLabel);
    const sizeCandidate = this.getVariantSizeCandidate(item);
    const baseTitle = this.stripVariantMeasurementsFromTitle(productTitle, sizeCandidate.label || variantTitle) || productTitle;
    return sizeCandidate.label ? `${baseTitle} - ${sizeCandidate.label}` : baseTitle;
  }
  normalizeWheelDiameter(value) {
    const match = String(value || '').match(/(\d{2})\s*mm/i);
    if (!match) return '';
    const diameter = Number.parseInt(match[1], 10);
    if (!Number.isFinite(diameter) || diameter < 40 || diameter > 80) return '';
    return `${diameter}mm`;
  }
  normalizeWheelHardness(value) {
    const match = String(value || '').match(/(\d{2,3})\s*([ab])/i);
    if (!match) return '';
    const hardness = Number.parseInt(match[1], 10);
    if (!Number.isFinite(hardness) || hardness < 70 || hardness > 110) return '';
    return `${hardness}${match[2].toUpperCase()}`;
  }
  enrichFilterItem(item, type) {
    const tags = this.normalizeTagList(item?.tags);
    const name = item?.name || '';
    const specs = { ...(item?.specs || {}) };
    if (type === 'deck') {
      specs.width =
        this.normalizeWidthValue(specs.width) ||
        this.normalizeWidthValue(this.getBuilderTagValue(tags, 'builder_width')) ||
        this.normalizeWidthValue(item?.variantTitle) ||
        this.extractWidthFromName(item?.variantTitle, false) ||
        this.extractWidthFromName(name, false);
    } else if (type === 'truck') {
      specs.width = this.normalizeWidthValue(specs.width) || this.normalizeWidthValue(this.getBuilderTagValue(tags, 'builder_width')) || this.extractTruckWidthFromName(name);
      specs.fitRange = this.normalizeDeckFitRange(
        specs.fitRange ||
        item?.fitRange ||
        this.getBuilderTagValue(tags, 'compatible_deck_width_range') ||
        this.getBuilderTagValue(tags, 'deck_width_range') ||
        this.getBuilderTagValue(tags, 'builder_fit_range') ||
        this.getBuilderTagValue(tags, 'truck_fit_range')
      );
    } else if (type === 'wheel') {
      specs.diameter = this.normalizeWheelDiameter(specs.diameter) || this.normalizeWheelDiameter(this.getBuilderTagValue(tags, 'builder_diameter')) || this.normalizeWheelDiameter(name);
      specs.hardness = this.normalizeWheelHardness(specs.hardness) || this.normalizeWheelHardness(this.getBuilderTagValue(tags, 'builder_hardness')) || this.normalizeWheelHardness(name);
    }
    const enriched = { ...item, type: item?.type || type, tags, specs };
    return { ...enriched, displayName: this.getItemDisplayName(enriched) };
  }
  enrichFilterItems(items, type) {
    return Array.isArray(items) ? items.map(item => this.enrichFilterItem(item, type)) : [];
  }
  getDeckVariantText(variant = {}) {
    return [
      variant.title,
      ...(Array.isArray(variant.options) ? variant.options : [])
    ].filter(Boolean).join(' ');
  }
  getDeckVariantWidth(variant = {}, item = {}) {
    const optionWidth = Array.isArray(variant.options)
      ? variant.options.map(option => this.normalizeWidthValue(option) || this.extractWidthFromName(option, false)).find(Boolean)
      : '';
    return (
      this.normalizeWidthValue(variant.width) ||
      optionWidth ||
      this.normalizeWidthValue(variant.title) ||
      this.extractWidthFromName(variant.title, false) ||
      this.normalizeWidthValue(item?.specs?.width) ||
      this.extractWidthFromName(item?.name, false)
    );
  }
  getDeckVariantPreferenceScore(variant = {}) {
    const text = this.getDeckVariantText(variant).toLowerCase();
    let score = 0;
    if (!text || text === 'default title') score += 2;
    if (/(^|[\s/-])(?:no|without)\s*grip\b|(?:no|without)\s*griptape|deck\s*only/.test(text)) score += 30;
    if (/\b(add|with)\s*grip\b|\bgrip\s*tape\b|\bgriptape\b/.test(text)) score -= 20;
    return score;
  }
  getDeckSizeChoices(item = {}) {
    const variants = Array.isArray(item.deckVariants) ? item.deckVariants : [];
    const byWidth = new Map();
    variants.forEach(variant => {
      const width = this.getDeckVariantWidth(variant, item);
      if (!width) return;
      const score = this.getDeckVariantPreferenceScore(variant);
      const existing = byWidth.get(width);
      if (!existing || score > existing.score) {
        byWidth.set(width, { width, variant, score });
      }
    });
    if (!byWidth.size) {
      const width = this.normalizeWidthValue(item?.specs?.width) || this.extractWidthFromName(item?.name, false);
      if (width) byWidth.set(width, { width, variant: null, score: 0 });
    }
    if (!byWidth.size && variants.length) {
      const fallbackVariant = variants.reduce((best, variant) => {
        return this.getDeckVariantPreferenceScore(variant) > this.getDeckVariantPreferenceScore(best) ? variant : best;
      }, variants[0]);
      byWidth.set('default', { width: '', variant: fallbackVariant, score: this.getDeckVariantPreferenceScore(fallbackVariant) });
    }
    return Array.from(byWidth.values()).sort((a, b) => {
      const aNum = Number.parseFloat(a.width);
      const bNum = Number.parseFloat(b.width);
      if (Number.isFinite(aNum) && Number.isFinite(bNum)) return aNum - bNum;
      if (Number.isFinite(aNum)) return -1;
      if (Number.isFinite(bNum)) return 1;
      return 0;
    });
  }
  getActiveDeckFilterWidth() {
    const value = this.activeDrawerFilters?.deck?.width;
    return value && value !== 'all' ? this.normalizeWidthValue(value) : '';
  }
  getDeckItemForVariant(item, choice) {
    if (!choice) return item;
    const variant = choice.variant || {};
    const width = choice.width || this.getDeckVariantWidth(variant, item);
    const productTitle = item.productTitle || item.name || '';
    const variantTitle = width ? this.formatSpecValue('width', width) : '';
    const displayName = this.getItemDisplayName({ ...item, productTitle, variantTitle }) || productTitle;
    return this.enrichFilterItem({
      ...item,
      uid: variant.variantId ? `${item.uid}-${variant.variantId}` : item.uid,
      productUid: item.productUid || item.uid,
      name: displayName,
      displayName,
      productTitle,
      variantTitle,
      selectedSize: width,
      variantId: variant.variantId || item.variantId,
      price: variant.price || item.price,
      texture: variant.texture || item.texture,
      specs: { ...(item.specs || {}), width },
      imageUrl: variant.imageUrl || item.imageUrl,
      fullImageUrl: variant.fullImageUrl || item.fullImageUrl,
      deckVariants: item.deckVariants || []
    }, 'deck');
  }
  resolveDeckVariantSelection(item, preferredVariantId = null, shouldPrompt = true) {
    const choices = this.getDeckSizeChoices(item);
    if (!choices.length) return item;
    let choice = null;
    if (preferredVariantId) {
      choice = choices.find(option => String(option.variant?.variantId || '') === String(preferredVariantId));
    }
    const filterWidth = this.getActiveDeckFilterWidth();
    if (!choice && filterWidth) {
      choice = choices.find(option => option.width === filterWidth);
    }
    if (!choice && (choices.length === 1 || !shouldPrompt)) {
      choice = choices[0];
    }
    if (!choice) {
      const lines = choices.map((option, index) => `${index + 1}. ${this.formatSpecValue('width', option.width)}`);
      const answer = window.prompt(`Choose deck size:\n${lines.join('\n')}`, '1');
      if (answer === null) return null;
      const normalizedAnswer = this.normalizeWidthValue(answer);
      const answerIndex = Number.parseInt(answer, 10) - 1;
      choice = Number.isInteger(answerIndex) && choices[answerIndex]
        ? choices[answerIndex]
        : choices.find(option => option.width === normalizedAnswer);
      if (!choice) {
        alert('Please choose one of the listed deck sizes.');
        return null;
      }
    }
    return this.getDeckItemForVariant(item, choice);
  }
  resolveDeckFromParam(deckParam) {
    if (!deckParam) return null;
    const exact = this.decks.find(deck => deck.uid === deckParam);
    if (exact) return this.resolveDeckVariantSelection(exact, null, false);
    const product = this.decks
      .filter(deck => String(deckParam).startsWith(`${deck.uid}-`))
      .sort((a, b) => String(b.uid).length - String(a.uid).length)[0];
    if (!product) return null;
    const preferredVariantId = String(deckParam).slice(String(product.uid).length + 1);
    return this.resolveDeckVariantSelection(product, preferredVariantId, false);
  }
  getItemFilterValues(item, field, type) {
    if (type === 'deck' && field === 'width') {
      const values = this.getDeckSizeChoices(item).map(choice => choice.width).filter(Boolean);
      return [...new Set(values)];
    }
    const value = item?.specs?.[field];
    return value ? [value] : [];
  }
  connectedCallback() {
    this.updateDesktopScale();
    this.initState();
    this.renderUI();
    this.initCollectionDrawers();
    this.initDrawerFilters();
    this.initBuilderMenu();
    this.initSummaryControls();
    this.initCartDrawer();
    this.initThree();
    this.preloadTexture(this.selectedDeck.texture);
    this.preloadTexture(this.selectedGrip.texture);
    this.preloadTexture(this.selectedWheel.texture);
    window.setTimeout(() => this.preloadGripTextures(), 350);
    this.loadAssets();
    const addToCartBtn = this.querySelector('#bc-add-to-cart-btn');
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', (event) => {
        event.preventDefault();
        this.handleAddToCart();
      });
    }
    this.initAssemblyChoice();
    const resetCameraBtn = this.querySelector('#bc-reset-camera-btn');
    if (resetCameraBtn) {
      resetCameraBtn.addEventListener('click', () => {
        this.resetCamera();
        resetCameraBtn.classList.add('is-pressed');
        window.setTimeout(() => {
          resetCameraBtn.classList.remove('is-pressed');
          resetCameraBtn.blur?.();
        }, 140);
      });
    }
    const resetBuildBtn = this.querySelector('#bc-reset-build-btn');
    if (resetBuildBtn) {
      resetBuildBtn.addEventListener('click', (event) => {
        event.preventDefault();
        if (this.hasChosenParts() && !window.confirm('Reset your board and clear all selected parts?')) return;
        this.resetBuild();
      });
    }
    this.querySelectorAll('[data-close-modal]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.closeImageModal();
      });
    });
    this.querySelectorAll('[data-image-modal-view]').forEach(button => {
      button.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        this.setImageModalView(button.getAttribute('data-image-modal-view') || 'image');
      });
    });
    const runImageModalNav = nav => {
      if (nav.matches('[data-image-modal-prev]')) {
        this.showImageModalImage(this.imageModalIndex - 1);
      } else if (nav.matches('[data-image-modal-next]')) {
        this.showImageModalImage(this.imageModalIndex + 1);
      } else {
        this.showImageModalImage(Number.parseInt(nav.getAttribute('data-image-modal-index'), 10));
      }
      nav.classList.add('is-pressed');
      window.setTimeout(() => {
        nav.classList.remove('is-pressed');
        nav.blur?.();
      }, 180);
    };
    this.querySelector('#bc-image-modal')?.addEventListener('click', event => {
      const nav = event.target.closest('[data-image-modal-prev], [data-image-modal-next], [data-image-modal-index]');
      if (!nav) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      runImageModalNav(nav);
    }, true);
    this.initImageModalSwipe();
    window.addEventListener('resize', this.handleWindowResize);
    const canvasContainer = this.querySelector('#bc-canvas-container');
    if (canvasContainer && 'ResizeObserver' in window) {
      this.canvasResizeObserver = new ResizeObserver(() => this.handleResize());
      this.canvasResizeObserver.observe(canvasContainer);
    }
    requestAnimationFrame(() => this.handleResize());
  }
  initAssemblyChoice() {
    const assemblyControl = this.querySelector('#bc-assembly');
    const buttons = Array.from(this.querySelectorAll('[data-assembly-choice-option]'));
    if (!assemblyControl || buttons.length === 0) return;
    const optionValues = Array.from(assemblyControl.options).map(option => option.value);
    const setAssemblyValue = (value) => {
      const nextValue = optionValues.includes(value) ? value : 'self';
      assemblyControl.value = nextValue;
      buttons.forEach(button => {
        const isActive = button.getAttribute('data-assembly-choice-option') === nextValue;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-checked', isActive ? 'true' : 'false');
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    };
    buttons.forEach(button => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        setAssemblyValue(button.getAttribute('data-assembly-choice-option') || 'self');
      });
    });
    assemblyControl.addEventListener('change', () => setAssemblyValue(assemblyControl.value || 'self'));
    setAssemblyValue(assemblyControl.value || 'self');
  }
  disconnectedCallback() {
    window.removeEventListener('resize', this.handleWindowResize);
    if (this.cartViewportListenersBound) {
      window.removeEventListener('resize', this.handleCartViewportResize);
      window.visualViewport?.removeEventListener('resize', this.handleCartViewportResize);
      window.visualViewport?.removeEventListener('scroll', this.handleCartViewportResize);
      this.cartViewportListenersBound = false;
    }
    this.canvasResizeObserver?.disconnect();
    this.canvasResizeObserver = null;
    this.drawerImageObservers.forEach(observer => observer.disconnect());
    this.drawerImageObservers.clear();
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    while (this.scrollLockCount > 0) this.unlockPageScroll();
    this.controls?.dispose?.();
    this.controls = null;
    this.renderer?.domElement?.remove();
    this.renderer?.dispose?.();
    this.renderer = null;
    this.scene = null;
    this.camera = null;
  }
  bindBuilderTap(node, handler, options = {}) {
    if (!node) return;
    let pointerTap = null;
    let suppressClickUntil = 0;
    const maxTapMove = 24;
    const triggerOn = options.triggerOn === 'press' ? 'press' : 'release';
    const runHandler = (event) => {
      if (event?.cancelable) event.preventDefault();
      if (typeof event?.stopPropagation === 'function') event.stopPropagation();
      handler(event);
    };
    node.addEventListener('pointerdown', (event) => {
      if (event.button !== undefined && event.button !== 0) return;
      pointerTap = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY
      };
      try {
        node.setPointerCapture?.(event.pointerId);
      } catch (error) {
        // Click fallback covers browsers that cannot capture this pointer.
      }
      if (triggerOn === 'press') {
        suppressClickUntil = performance.now() + 650;
        runHandler(event);
      }
    }, { passive: false });
    node.addEventListener('pointerup', (event) => {
      if (!pointerTap || pointerTap.id !== event.pointerId) return;
      const deltaX = event.clientX - pointerTap.x;
      const deltaY = event.clientY - pointerTap.y;
      const moved = Math.hypot(deltaX, deltaY);
      pointerTap = null;
      try {
        node.releasePointerCapture?.(event.pointerId);
      } catch (error) {
        // Ignore stale pointer capture releases.
      }
      if (triggerOn === 'press') {
        if (event?.cancelable) event.preventDefault();
        if (typeof event?.stopPropagation === 'function') event.stopPropagation();
        return;
      }
      suppressClickUntil = performance.now() + 650;
      if (moved > maxTapMove) {
        if (event?.cancelable) event.preventDefault();
        if (typeof event?.stopPropagation === 'function') event.stopPropagation();
        return;
      }
      runHandler(event);
    }, { passive: false });
    node.addEventListener('pointercancel', (event) => {
      pointerTap = null;
      suppressClickUntil = performance.now() + 650;
      try {
        node.releasePointerCapture?.(event.pointerId);
      } catch (error) {
        // Ignore stale pointer capture releases.
      }
    });
    node.addEventListener('click', (event) => {
      if (performance.now() < suppressClickUntil) {
        if (event?.cancelable) event.preventDefault();
        if (typeof event?.stopPropagation === 'function') event.stopPropagation();
        return;
      }
      runHandler(event);
    });
  }
  syncCartDrawerViewportHeight() {
    const height = Math.ceil(
      window.visualViewport?.height ||
      window.innerHeight ||
      document.documentElement.clientHeight ||
      0
    );
    if (height > 0) {
      this.style.setProperty('--cart-drawer-viewport-height', `${height}px`);
    }
  }
  lockPageScroll({ fixedBody = true } = {}) {
    if (this.scrollLockCount > 0) {
      this.scrollLockCount += 1;
      return;
    }
    this.scrollLockY = window.scrollY || document.documentElement.scrollTop || 0;
    this.scrollLockUsesFixedBody = fixedBody;
    const scrollbarComp = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.setProperty('--scrollbar-comp', `${scrollbarComp}px`);
    document.documentElement.classList.add('bc-builder-menu-lock');
    document.body.classList.add('bc-builder-menu-lock');
    document.body.classList.toggle('bc-builder-menu-lock-fixed', fixedBody);
    if (fixedBody) {
      document.body.style.setProperty('--scroll-lock-top', `-${this.scrollLockY}px`);
    } else {
      document.body.style.removeProperty('--scroll-lock-top');
    }
    this.scrollLockCount = 1;
  }
  unlockPageScroll() {
    if (this.scrollLockCount === 0) return;
    this.scrollLockCount -= 1;
    if (this.scrollLockCount > 0) return;
    const shouldRestoreFixedBodyScroll = this.scrollLockUsesFixedBody;
    document.documentElement.classList.remove('bc-builder-menu-lock');
    document.body.classList.remove('bc-builder-menu-lock');
    document.body.classList.remove('bc-builder-menu-lock-fixed');
    document.body.style.removeProperty('--scroll-lock-top');
    document.body.style.removeProperty('--scrollbar-comp');
    if (shouldRestoreFixedBodyScroll) {
      window.scrollTo(0, this.scrollLockY);
    }
    this.scrollLockUsesFixedBody = false;
  }
  initState() {
    const urlParams = new URLSearchParams(window.location.search);
    const deckParam = urlParams.get('deck');
    this.selectedDeck = this.resolveDeckFromParam(deckParam) || this.createEmptySelection('deck');
    const gripParam = urlParams.get('grip');
    this.selectedGrip = this.grips.find(g => g.uid === gripParam) || this.createEmptySelection('grip');
    const wheelParam = urlParams.get('wheel');
    this.selectedWheel = this.wheels.find(w => w.uid === wheelParam) || this.createEmptySelection('wheel');
    const bearingParam = urlParams.get('bearing');
    this.selectedBearing = this.bearings.find(b => b.uid === bearingParam) || this.createEmptySelection('bearing');
    const truckParam = urlParams.get('truck');
    this.selectedTruck = this.trucks.find(t => t.uid === truckParam) || this.trucks.find(t => truckParam && t.uid.startsWith(`${truckParam}-`)) || this.createEmptySelection('truck');
    const hardwareParam = urlParams.get('hardware') || urlParams.get('bolt');
    this.selectedHardware = this.hardware.find(h => h.uid === hardwareParam) || this.bolts.find(b => b.uid === hardwareParam) || this.createEmptySelection('hardware');
  }
  createEmptySelection(type) {
    const labels = {
      deck: 'Choose a deck',
      grip: 'Choose grip',
      wheel: 'Choose wheels',
      bearing: 'Choose bearings',
      truck: 'Choose trucks',
      hardware: 'Choose hardware'
    };
    const defaults = {
      deck: { texture: this.defaultDeckUrl },
      grip: { texture: this.defaultGripUrl, color: this.defaultGripColor },
      wheel: { texture: this.defaultWheelUrl },
      bearing: { color: this.defaultBearingColor },
      truck: {
        color: this.defaultTruckColor,
        baseplateColor: this.defaultTruckColor,
        bushingTopColor: '#111111',
        bushingBottomColor: '#111111',
        washerTopColor: this.defaultTruckColor,
        washerBottomColor: this.defaultTruckColor
      },
      hardware: {
        color: this.defaultHardwareColor || this.defaultBoltColor,
        axleNutColor: '#050505',
        kingpinNutColor: '#050505'
      }
    };
    return {
      uid: `__empty_${type}`,
      name: labels[type],
      isPlaceholder: true,
      ...defaults[type]
    };
  }
  renderModalDescription(target, html = '', fallbackText = '') {
    if (!target) return;
    const fallback = String(fallbackText || '').trim() || 'No product description is available yet. Use the product image for the closest visual reference.';
    const rawHtml = String(html || '').trim();
    const allowedTags = new Set(['A', 'B', 'BR', 'DIV', 'EM', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'I', 'LI', 'OL', 'P', 'SPAN', 'STRONG', 'TABLE', 'TBODY', 'TD', 'TH', 'THEAD', 'TR', 'U', 'UL']);
    const appendFallback = () => {
      const paragraph = document.createElement('p');
      paragraph.textContent = fallback;
      target.replaceChildren(paragraph);
    };
    if (!rawHtml) {
      appendFallback();
      return;
    }
    const sanitizeNode = node => {
      if (node.nodeType === Node.TEXT_NODE) return document.createTextNode(node.textContent || '');
      if (node.nodeType !== Node.ELEMENT_NODE) return document.createDocumentFragment();
      const tagName = node.tagName.toUpperCase();
      const children = Array.from(node.childNodes).map(sanitizeNode);
      if (!allowedTags.has(tagName)) {
        const fragment = document.createDocumentFragment();
        children.forEach(child => fragment.appendChild(child));
        return fragment;
      }
      const cleanElement = document.createElement(tagName.toLowerCase());
      if (tagName === 'A') {
        const href = node.getAttribute('href') || '';
        if (/^(https?:|mailto:|tel:|\/)/i.test(href)) {
          cleanElement.setAttribute('href', href);
          cleanElement.setAttribute('target', '_blank');
          cleanElement.setAttribute('rel', 'noopener noreferrer');
        }
      } else if (['TD', 'TH'].includes(tagName)) {
        ['colspan', 'rowspan'].forEach(attr => {
          const value = node.getAttribute(attr);
          if (value && /^\d{1,2}$/.test(value)) cleanElement.setAttribute(attr, value);
        });
      }
      children.forEach(child => cleanElement.appendChild(child));
      return cleanElement;
    };
    const template = document.createElement('template');
    template.innerHTML = rawHtml;
    const fragment = document.createDocumentFragment();
    Array.from(template.content.childNodes).map(sanitizeNode).forEach(node => fragment.appendChild(node));
    if (!fragment.textContent.trim()) {
      appendFallback();
      return;
    }
    target.replaceChildren(fragment);
  }
  parseImageList(value) {
    if (!value || value === 'null') return [];
    if (Array.isArray(value)) return value;
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
      return [];
    }
  }
  normalizeModalImages(src, previewSrc = '', images = [], alt = 'Part Image') {
    const gallery = [];
    const seen = new Set();
    const entries = [
      { src, previewSrc, alt },
      ...this.parseImageList(images)
    ];
    entries.forEach(entry => {
      const item = typeof entry === 'string' ? { src: entry } : (entry || {});
      const fullSrc = String(item.src || item.fullImageUrl || item.url || '').trim();
      if (!fullSrc || fullSrc === 'null' || seen.has(fullSrc)) return;
      seen.add(fullSrc);
      gallery.push({
        src: fullSrc,
        previewSrc: String(item.previewSrc || item.imageUrl || item.thumbnail || previewSrc || fullSrc).trim(),
        alt: String(item.alt || alt || 'Product image').trim()
      });
    });
    return gallery;
  }
  renderImageModalGalleryControls() {
    const modal = this.querySelector('#bc-image-modal');
    const prevButton = this.querySelector('[data-image-modal-prev]');
    const nextButton = this.querySelector('[data-image-modal-next]');
    const dots = this.querySelector('[data-image-modal-dots]');
    const hasMultipleImages = this.imageModalImages.length > 1;
    modal?.classList.toggle('has-gallery', hasMultipleImages);
    [prevButton, nextButton].forEach(button => {
      if (!button) return;
      button.hidden = !hasMultipleImages;
      button.disabled = !hasMultipleImages;
    });
    if (!dots) return;
    dots.hidden = !hasMultipleImages;
    dots.replaceChildren(...this.imageModalImages.map((_image, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = `bc-image-modal__dot${index === this.imageModalIndex ? ' is-active' : ''}`;
      dot.setAttribute('data-image-modal-index', String(index));
      dot.setAttribute('aria-label', `Show product image ${index + 1}`);
      dot.setAttribute('aria-current', index === this.imageModalIndex ? 'true' : 'false');
      return dot;
    }));
  }
  showImageModalImage(index) {
    const modal = this.querySelector('#bc-image-modal');
    const modalImg = this.querySelector('#bc-image-modal-img');
    if (!modal || !modalImg || !this.imageModalImages.length) return;
    const total = this.imageModalImages.length;
    this.imageModalIndex = ((index % total) + total) % total;
    const image = this.imageModalImages[this.imageModalIndex];
    const requestId = ++this.imageModalRequestId;
    modalImg.alt = image.alt || 'Product image';
    this.renderImageModalGalleryControls();
    if (this.loadedDrawerImages.has(image.src)) {
      modalImg.src = image.src;
      modal.classList.remove('is-loading');
      this.preloadImageModalNeighbors();
      return;
    }
    if (image.previewSrc && image.previewSrc !== 'null') {
      modalImg.src = image.previewSrc;
      modal.classList.remove('is-loading');
    } else {
      modalImg.removeAttribute('src');
      modal.classList.add('is-loading');
    }
    this.preloadDrawerImage(image.src).then(() => {
      if (requestId !== this.imageModalRequestId) return;
      modalImg.src = image.src;
      modal.classList.remove('is-loading');
      this.preloadImageModalNeighbors();
    }).catch(() => {
      if (requestId !== this.imageModalRequestId) return;
      modalImg.src = image.src;
      modal.classList.remove('is-loading');
      this.preloadImageModalNeighbors();
    });
    this.preloadImageModalNeighbors();
  }
  preloadImageModalNeighbors() {
    const total = this.imageModalImages.length;
    if (total < 2) return;
    [this.imageModalIndex - 1, this.imageModalIndex + 1].forEach(index => {
      const image = this.imageModalImages[((index % total) + total) % total];
      if (image?.previewSrc && image.previewSrc !== 'null') this.preloadDrawerImage(image.previewSrc);
      if (image?.src && image.src !== 'null') this.preloadDrawerImage(image.src);
    });
  }
  preloadImageModalGallery() {
    this.imageModalImages.forEach(image => {
      if (image?.previewSrc && image.previewSrc !== 'null') this.preloadDrawerImage(image.previewSrc);
      if (image?.src && image.src !== 'null') this.preloadDrawerImage(image.src);
    });
  }
  initImageModalSwipe() {
    const swipeSurface = this.querySelector('.bc-image-modal__media');
    if (!swipeSurface) return;
    swipeSurface.addEventListener('pointerdown', event => {
      if (event.target.closest?.('.bc-image-modal__nav, .bc-image-modal__dot')) return;
      if (this.imageModalImages.length < 2 || event.button !== 0) return;
      this.imageModalSwipe = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        lastX: event.clientX,
        lastY: event.clientY,
        swiping: false
      };
      swipeSurface.setPointerCapture?.(event.pointerId);
    });
    swipeSurface.addEventListener('pointermove', event => {
      const swipe = this.imageModalSwipe;
      if (!swipe || swipe.pointerId !== event.pointerId) return;
      swipe.lastX = event.clientX;
      swipe.lastY = event.clientY;
      const deltaX = swipe.lastX - swipe.startX;
      const deltaY = swipe.lastY - swipe.startY;
      if (!swipe.swiping && Math.abs(deltaX) > 14 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
        swipe.swiping = true;
      }
      if (swipe.swiping) event.preventDefault();
    }, { passive: false });
    const finishSwipe = event => {
      const swipe = this.imageModalSwipe;
      if (!swipe || swipe.pointerId !== event.pointerId) return;
      const deltaX = swipe.lastX - swipe.startX;
      const deltaY = swipe.lastY - swipe.startY;
      this.imageModalSwipe = null;
      swipeSurface.releasePointerCapture?.(event.pointerId);
      if (Math.abs(deltaX) < 48 || Math.abs(deltaX) < Math.abs(deltaY) * 1.25) return;
      this.showImageModalImage(this.imageModalIndex + (deltaX < 0 ? 1 : -1));
    };
    swipeSurface.addEventListener('pointerup', finishSwipe);
    swipeSurface.addEventListener('pointercancel', () => {
      this.imageModalSwipe = null;
    });
  }
  openImageModal(src, alt = 'Part Image', previewSrc = '', description = '', descriptionHtml = '', images = []) {
    if (!src || src === 'null') return;
    this.clearHoveredProductCard();
    const modal = this.querySelector('#bc-image-modal');
    const modalImg = this.querySelector('#bc-image-modal-img');
    const modalTitle = this.querySelector('#bc-image-modal-title');
    const modalDescription = this.querySelector('#bc-image-modal-description');
    const modalViewport = this.querySelector('[data-image-modal-viewport]');
    if (!modal || !modalImg) return;
    this.imageModalLastFocusedElement = document.activeElement;
    const modalName = alt || 'Part Image';
    if (modalTitle) modalTitle.textContent = modalName;
    this.renderModalDescription(modalDescription, descriptionHtml, description);
    this.imageModalImages = this.normalizeModalImages(src, previewSrc, images, modalName);
    this.imageModalIndex = 0;
    this.setImageModalView('image');
    if (modalViewport) {
      modalViewport.scrollTop = 0;
      modalViewport.scrollLeft = 0;
    }
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    this.showImageModalImage(0);
    this.preloadImageModalGallery();
  }
  preloadDrawerImage(src) {
    if (!src || src === 'null') return Promise.resolve(null);
    if (this.loadedDrawerImages.has(src)) return Promise.resolve(src);
    if (this.imagePreloadPromises.has(src)) return this.imagePreloadPromises.get(src);
    const preloadPromise = new Promise(resolve => {
      const image = new Image();
      image.onload = () => {
        this.loadedDrawerImages.add(src);
        this.imagePreloadPromises.delete(src);
        resolve(src);
      };
      image.onerror = () => {
        this.imagePreloadPromises.delete(src);
        resolve(null);
      };
      image.src = src;
    });
    this.imagePreloadPromises.set(src, preloadPromise);
    return preloadPromise;
  }
  primeProductCardImage(card, priority = 'auto') {
    const img = card?.querySelector?.('.bc-product-card__image-wrapper img');
    if (!img) return;
    img.loading = 'eager';
    img.decoding = 'async';
    if ('fetchPriority' in img) img.fetchPriority = priority;
    const src = img.currentSrc || img.src || img.getAttribute('src');
    if (!src || src === 'null') return;
    if (img.complete && img.naturalWidth > 0) {
      this.loadedDrawerImages.add(src);
      return;
    }
    if (img.dataset.bcPreviewPrimed === 'true') return;
    img.dataset.bcPreviewPrimed = 'true';
    this.preloadDrawerImage(src);
  }
  getPrimeableDrawerCards(type) {
    const drawer = this.querySelector(`#bc-drawer-${type}`);
    if (!drawer || !drawer.classList.contains('is-open')) return [];
    return Array.from(drawer.querySelectorAll(`.bc-product-card[data-type="${type}"]`))
      .filter(card => !card.classList.contains('is-filtered-out'));
  }
  primeDrawerPreviewImages(type) {
    const drawer = this.querySelector(`#bc-drawer-${type}`);
    const body = drawer?.querySelector('.bc-drawer__body');
    if (!drawer || !body || !drawer.classList.contains('is-open')) return;
    const cards = this.getPrimeableDrawerCards(type);
    if (!cards.length) return;
    const rootRect = body.getBoundingClientRect();
    const lead = Math.max((body.clientHeight || window.innerHeight || 720) * 1.65, 760);
    let primed = 0;
    cards.forEach(card => {
      const rect = card.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      if (rect.bottom < rootRect.top - lead || rect.top > rootRect.bottom + lead) return;
      this.primeProductCardImage(card, primed < 8 ? 'high' : 'auto');
      primed += 1;
    });
    if (primed > 0) return;
    cards.slice(0, 12).forEach((card, index) => {
      this.primeProductCardImage(card, index < 8 ? 'high' : 'auto');
    });
  }
  primeDrawerImages(type) {
    const drawer = this.querySelector(`#bc-drawer-${type}`);
    const body = drawer?.querySelector('.bc-drawer__body');
    if (!drawer || !body || !drawer.classList.contains('is-open')) return;
    const existingObserver = this.drawerImageObservers.get(type);
    if (existingObserver) existingObserver.disconnect();
    this.drawerImageObservers.delete(type);
    requestAnimationFrame(() => this.primeDrawerPreviewImages(type));
    if (!('IntersectionObserver' in window)) return;
    const lead = Math.round(Math.max((body.clientHeight || window.innerHeight || 720) * 1.75, 900));
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting && entry.intersectionRatio <= 0) return;
        this.primeProductCardImage(entry.target);
        observer.unobserve(entry.target);
      });
    }, { root: body, rootMargin: `${lead}px 0px`, threshold: 0 });
    this.getPrimeableDrawerCards(type).forEach(card => observer.observe(card));
    this.drawerImageObservers.set(type, observer);
  }
  closeImageModal() {
    const modal = this.querySelector('#bc-image-modal');
    if (!modal) return;
    this.imageModalRequestId += 1;
    if (modal.contains(document.activeElement)) {
      let fallback = this.imageModalLastFocusedElement && !modal.contains(this.imageModalLastFocusedElement)
        ? this.imageModalLastFocusedElement
        : this.querySelector('#bc-summary-toggle');
      if (fallback?.closest?.('.bc-product-card')) {
        fallback = this.querySelector('.bc-drawer.is-open .bc-drawer__close') || this.querySelector('#bc-summary-toggle');
      }
      fallback?.focus?.({ preventScroll: true });
    }
    modal.classList.remove('is-open');
    modal.classList.remove('is-loading');
    modal.classList.remove('has-gallery');
    modal.setAttribute('aria-hidden', 'true');
    this.imageModalImages = [];
    this.imageModalIndex = 0;
    this.imageModalSwipe = null;
    this.clearHoveredProductCard();
  }
  setImageModalView(view = 'image') {
    const modal = this.querySelector('#bc-image-modal');
    if (!modal) return;
    const activeView = view === 'details' ? 'details' : 'image';
    modal.setAttribute('data-mobile-view', activeView);
    this.querySelectorAll('[data-image-modal-view]').forEach(button => {
      const isActive = button.getAttribute('data-image-modal-view') === activeView;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
    const modalViewport = this.querySelector('[data-image-modal-viewport]');
    if (modalViewport) {
      modalViewport.scrollTop = 0;
      modalViewport.scrollLeft = 0;
    }
  }
  setHoveredProductCard(card) {
    if (this.hoveredProductCard === card) return;
    if (this.hoveredProductCard) this.hoveredProductCard.classList.remove('is-hovering');
    this.hoveredProductCard = card && this.contains(card) ? card : null;
    if (this.hoveredProductCard) this.hoveredProductCard.classList.add('is-hovering');
  }
  clearHoveredProductCard() {
    this.hoveredProductCard = null;
    this.querySelectorAll('.bc-product-card.is-hovering').forEach(card => card.classList.remove('is-hovering'));
  }
  hasChosenParts() {
    return [this.selectedDeck, this.selectedGrip, this.selectedWheel, this.selectedBearing, this.selectedTruck, this.selectedHardware]
      .some(part => part && !part.isPlaceholder);
  }
  resetBuild() {
    this.selectedDeck = this.createEmptySelection('deck');
    this.selectedGrip = this.createEmptySelection('grip');
    this.selectedWheel = this.createEmptySelection('wheel');
    this.selectedBearing = this.createEmptySelection('bearing');
    this.selectedTruck = this.createEmptySelection('truck');
    this.selectedHardware = this.createEmptySelection('hardware');
    ['deck', 'grip', 'wheel', 'bearing', 'truck', 'hardware', 'bolt'].forEach(key => this.removeUrlParam(key));
    this.updateDeckTexture(this.selectedDeck.texture);
    this.updateGripTexture(this.selectedGrip.texture);
    this.updateGripColor(this.selectedGrip.color);
    this.updateWheelTexture(this.selectedWheel.texture);
    this.updateBearingColor(this.selectedBearing.color);
    this.updateTruckColor(this.selectedTruck.color, this.selectedTruck.baseplateColor, this.selectedTruck);
    this.updateHardwareColors(this.selectedHardware);
    this.renderUI();
    this.resetCamera();
    this.querySelector('#bc-summary')?.classList.remove('is-expanded');
    this.querySelector('#bc-summary-toggle')?.setAttribute('aria-expanded', 'false');
    this.querySelector('#bc-summary-scrim')?.classList.remove('is-visible');
  }
  renderUI() {
    const defaultPartCopy = {
      deck: 'The wooden plank you stand on',
      grip: 'Sandpaper for your board',
      truck: 'The metal part that makes turning possible',
      wheel: 'The round things on your board. Pretty important.',
      bearing: 'Tiny circles that make your wheels go brrrr.',
      hardware: 'Keep your board in one piece'
    };
    const parts = [
      { key: 'deck', label: 'Deck', sel: this.selectedDeck },
      { key: 'grip', label: 'Griptape', sel: this.selectedGrip },
      { key: 'truck', label: 'Trucks', sel: this.selectedTruck },
      { key: 'wheel', label: 'Wheels', sel: this.selectedWheel },
      { key: 'bearing', label: 'Bearings', sel: this.selectedBearing },
      { key: 'hardware', label: 'Hardware', sel: this.selectedHardware }
    ];
    const listEl = this.querySelector('#bc-summary-list');
    const countEl = this.querySelector('#bc-summary-count');
    const builderProgressEl = this.querySelector('#bc-builder-progress');
    let count = 0;
    let summaryHtml = '';
    parts.forEach(p => {
      const isChosen = p.sel && !p.sel.isPlaceholder;
      if (isChosen) count++;
      const valName = isChosen ? this.getItemDisplayName(p.sel) : 'Not selected';
      const valClass = isChosen ? 'bc-summary__val is-chosen' : 'bc-summary__val';
      const previewImage = isChosen ? (p.sel.imageUrl || '') : '';
      const fullImage = isChosen ? (p.sel.fullImageUrl || p.sel.imageUrl || '') : '';
      const description = isChosen ? (p.sel.description || '') : '';
      const descriptionHtml = isChosen ? (p.sel.descriptionHtml || '') : '';
      const modalImages = isChosen
        ? this.normalizeModalImages(fullImage, previewImage, p.sel.images || [], valName)
        : [];
      const imgSrcAttr = previewImage
        ? `data-img="${this.escapeHtml(previewImage)}" data-full-img="${this.escapeHtml(fullImage)}" data-description="${this.escapeHtml(description)}" data-description-html="${this.escapeHtml(descriptionHtml)}" data-images="${this.escapeHtml(JSON.stringify(modalImages))}"`
        : '';
      summaryHtml += `
        <li class="bc-summary__item" data-drawer="${p.key}" ${imgSrcAttr} role="button" tabindex="0">
          <span class="bc-summary__type">${p.label}</span>
          <span class="${valClass}">${valName.replace(/-/g, ' ')}</span>
        </li>
      `;
      const rowBtn = this.querySelector(`[data-open-drawer="${p.key}"] .bc-builder-status`);
      const rowCopy = this.querySelector(`[data-open-drawer="${p.key}"] .bc-builder-copy`);
      const row = this.querySelector(`[data-open-drawer="${p.key}"]`);
      if (rowBtn) {
        rowBtn.textContent = isChosen ? 'Chosen' : 'Choose';
        rowBtn.title = isChosen ? valName : '';
      }
      if (rowCopy) {
        rowCopy.textContent = isChosen ? valName.replace(/-/g, ' ') : defaultPartCopy[p.key];
        rowCopy.title = rowCopy.textContent;
      }
      if (row) {
        row.classList.toggle('is-chosen', isChosen);
        row.setAttribute('aria-label', `${p.label}: ${valName}`);
      }
      if (previewImage) this.preloadDrawerImage(previewImage);
      if (fullImage && fullImage !== previewImage) {
        const warmFullImage = () => this.preloadDrawerImage(fullImage);
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(warmFullImage, { timeout: 1200 });
        } else {
          window.setTimeout(warmFullImage, 120);
        }
      }
    });
    if (count > 0) {
      summaryHtml = `<li class="bc-summary__hint">Tap on the images for a closer look and product details.</li>` + summaryHtml;
    }
    if (listEl) listEl.innerHTML = summaryHtml;
    if (countEl) countEl.textContent = `${count}/6`;
    if (builderProgressEl) builderProgressEl.textContent = `${count}/6 selected`;
    if (listEl) {
      listEl.querySelectorAll('.bc-summary__item').forEach(el => {
        const openReceiptImage = () => {
          const previewSrc = el.getAttribute('data-img');
          const fullSrc = el.getAttribute('data-full-img') || previewSrc;
          if (previewSrc && previewSrc !== 'null' && previewSrc !== '') {
            this.openImageModal(
              fullSrc,
              el.querySelector('.bc-summary__val')?.textContent || 'Part Image',
              previewSrc,
              el.getAttribute('data-description') || '',
              el.getAttribute('data-description-html') || '',
              this.parseImageList(el.getAttribute('data-images'))
            );
          }
        };
        el.addEventListener('click', openReceiptImage);
        el.addEventListener('keydown', event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openReceiptImage();
          }
        });
      });
    }
    this.updateActiveProductCards();
  }
  initBuilderMenu() {
    const menu = this.querySelector('[data-bc-menu]');
    const openBtn = this.querySelector('[data-bc-menu-open]');
    const closeBtn = this.querySelector('[data-bc-menu-close]');
    if (!menu || !openBtn || !closeBtn) return;
    this.upgradeLegacyBuilderMenu(menu);
    const mainMenuPanel = menu?.querySelector('[data-main-menu-panel]');
    const shopMenuPanel = menu?.querySelector('[data-shop-menu-panel]');
    const menuCloseTriggers = menu?.querySelectorAll('[data-bc-menu-close]') || [];
    const shopTriggers = menu?.querySelectorAll('[data-bc-shop-drill]') || [];
    const shopBackTriggers = menu?.querySelectorAll('[data-bc-shop-back]') || [];
    const menuMainTriggers = menu?.querySelectorAll('[data-bc-menu-main]') || [];
    const menuShopShortcuts = menu?.querySelectorAll('[data-bc-shop-shortcut]') || [];
    const isMenuOpen = () => menu.getAttribute('aria-hidden') === 'false';
    const updateMenuMode = (isNested) => {
      menu.classList.toggle('is-nested-open', isNested);
      shopTriggers.forEach(trigger => trigger.setAttribute('aria-expanded', isNested ? 'true' : 'false'));
      menuMainTriggers.forEach(trigger => trigger.setAttribute('aria-pressed', isNested ? 'false' : 'true'));
      menuShopShortcuts.forEach(trigger => trigger.setAttribute('aria-pressed', isNested ? 'true' : 'false'));
    };
    const closeNested = () => {
      updateMenuMode(false);
      if (shopMenuPanel) shopMenuPanel.scrollTop = 0;
    };
    const openNested = () => {
      updateMenuMode(true);
      if (mainMenuPanel) mainMenuPanel.scrollTop = 0;
    };
    const openMenu = () => {
      if (isMenuOpen()) return;
      this.menuLastFocusedElement = document.activeElement;
      menu.hidden = false;
      menu.removeAttribute('inert');
      this.lockPageScroll();
      closeNested();
      menu.setAttribute('aria-hidden', 'false');
      menu.classList.add('is-open');
      closeBtn.focus?.({ preventScroll: true });
    };
    const closeMenu = ({ restoreFocus = true } = {}) => {
      if (!isMenuOpen()) return;
      const focusTarget = this.menuLastFocusedElement && !menu.contains(this.menuLastFocusedElement)
        ? this.menuLastFocusedElement
        : openBtn;
      this.menuLastFocusedElement = null;
      menu.setAttribute('aria-hidden', 'true');
      menu.classList.remove('is-open');
      closeNested();
      this.unlockPageScroll();
      menu.setAttribute('inert', '');
      menu.hidden = true;
      if (restoreFocus && focusTarget && typeof focusTarget.focus === 'function') {
        focusTarget.focus({ preventScroll: true });
      }
    };
    this.bindBuilderTap(openBtn, () => {
      if (isMenuOpen()) return;
      openMenu();
    });
    menuCloseTriggers.forEach(trigger => trigger.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!isMenuOpen()) return;
      closeMenu();
    }));
    shopTriggers.forEach(trigger => this.bindBuilderTap(trigger, () => {
      openNested();
    }));
    shopBackTriggers.forEach(trigger => this.bindBuilderTap(trigger, () => {
      closeNested();
    }));
    menuMainTriggers.forEach(trigger => this.bindBuilderTap(trigger, () => {
      closeNested();
    }));
    menuShopShortcuts.forEach(trigger => this.bindBuilderTap(trigger, () => {
      openNested();
    }));
    menu.querySelectorAll('a').forEach(link => {
      link.addEventListener('click', closeMenu);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && isMenuOpen()) closeMenu();
    });
    menu.addEventListener('click', (event) => {
      if (event.target?.closest?.('.mobile-menu-inner')) return;
      closeMenu();
    });
  }
  initSummaryControls() {
    const summaryToggle = this.querySelector('#bc-summary-toggle');
    const summary = this.querySelector('#bc-summary');
    const summaryScrim = this.querySelector('#bc-summary-scrim');
    const summaryClose = this.querySelector('#bc-summary-close');
    if (!summaryToggle || !summary) return;

    summaryToggle.setAttribute('aria-expanded', summary.classList.contains('is-expanded') ? 'true' : 'false');
    const isDesktopSummaryMode = () => window.matchMedia('(min-width: 75rem) and (hover: hover) and (pointer: fine), (min-width: 56rem) and (orientation: landscape) and (hover: hover) and (pointer: fine)').matches;
    const syncSummary = () => {
      const isOpen = summary.classList.contains('is-expanded');
      summaryToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      summaryScrim?.classList.toggle('is-visible', isOpen && !isDesktopSummaryMode());
    };
    const closeSummary = () => {
      summary.classList.remove('is-expanded');
      syncSummary();
    };
    summaryToggle.addEventListener('click', (event) => {
      event.stopPropagation();
      if (summary.classList.contains('is-expanded')) {
        if (isDesktopSummaryMode() && event.target.closest?.('.bc-summary__icon')) {
          closeSummary();
        }
        return;
      }
      summary.classList.add('is-expanded');
      syncSummary();
    });
    summaryClose?.addEventListener('click', (event) => {
      event.stopPropagation();
      closeSummary();
    });
    summary.addEventListener('click', (event) => {
      event.stopPropagation();
    });
    summaryScrim?.addEventListener('click', (event) => {
      if (isDesktopSummaryMode()) return;
      if (event.target === summaryScrim) closeSummary();
    });
    document.addEventListener('click', (event) => {
      if (!summary.classList.contains('is-expanded')) return;
      if (isDesktopSummaryMode()) return;
      if (event.target.closest?.('.bc-summary')) return;
      if (event.target.closest?.('.bc-image-modal')) return;
      closeSummary();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && summary.classList.contains('is-expanded')) closeSummary();
    });
  }
  upgradeLegacyBuilderMenu(menu) {
    if (!menu) return;
    let didUpgrade = false;

    menu.querySelectorAll('.mobile-menu-heading, .mobile-menu-thumbbar').forEach(node => {
      node.remove();
      didUpgrade = true;
    });

    const navList = menu.querySelector('.mobile-main-panel .mobile-nav-list');
    if (navList) {
      const links = Array.from(navList.querySelectorAll('a'));
      const hasHomeLink = links.some(link => (link.textContent || '').trim().toLowerCase() === 'home');
      let homeItem = links.find(link => (link.textContent || '').trim().toLowerCase() === 'home')?.closest('.mobile-nav-item') || null;
      if (!hasHomeLink) {
        homeItem = document.createElement('li');
        homeItem.className = 'mobile-nav-item';
        homeItem.innerHTML = `<a href="${this.escapeHtml(this.rootUrl || '/')}" class="mobile-nav-link">Home</a>`;
        navList.insertBefore(homeItem, navList.firstChild);
        didUpgrade = true;
      }

      if (!navList.querySelector('[data-bc-shop-drill]')) {
        const shopItem = document.createElement('li');
        shopItem.className = 'mobile-nav-item';
        shopItem.innerHTML = `
          <button type="button" class="mobile-root-button" data-bc-shop-drill aria-expanded="false">
            <span class="mobile-nav-link">Shop</span>
            <span class="mobile-root-icon" aria-hidden="true">&rsaquo;</span>
          </button>
        `;
        const insertAfter = homeItem || navList.firstElementChild;
        if (insertAfter?.nextSibling) {
          navList.insertBefore(shopItem, insertAfter.nextSibling);
        } else {
          navList.appendChild(shopItem);
        }
        didUpgrade = true;
      }
    }

    const shopPanel = menu.querySelector('[data-shop-menu-panel]');
    if (shopPanel && !shopPanel.querySelector('[data-bc-shop-back]')) {
      const backButton = document.createElement('button');
      backButton.className = 'mobile-shop-back-trigger';
      backButton.type = 'button';
      backButton.setAttribute('data-bc-shop-back', '');
      backButton.setAttribute('aria-label', 'Back to main menu');
      backButton.innerHTML = `
        <span class="mobile-shop-back-icon" aria-hidden="true">&lsaquo;</span>
        <span class="mobile-shop-back-heading">Back</span>
      `;
      shopPanel.insertBefore(backButton, shopPanel.firstChild);
      didUpgrade = true;
    }

    menu.classList.toggle('is-legacy-menu-upgraded', didUpgrade);
  }
  initCartDrawer() {
    this.cartDrawer = this.querySelector('[data-bc-cart-drawer]') || this.querySelector('#CartDrawer');
    this.cartDrawerPanel = this.querySelector('.cart-drawer-panel');
    this.cartDrawerBody = this.querySelector('[data-cart-drawer-body]');
    this.cartDrawerFooter = this.querySelector('[data-cart-drawer-footer]');
    this.cartDrawerTotal = this.querySelector('[data-cart-total]');
    this.cartClearButton = this.querySelector('[data-cart-clear]');
    this.cartClearConfirm = this.querySelector('[data-cart-clear-confirm]');
    this.cartClearConfirmAction = this.querySelector('[data-cart-clear-confirm-action]');
    this.cartTriggers = this.querySelectorAll('[data-bc-cart-drawer-trigger], [data-cart-drawer-trigger]');
    const cartCloseButtons = this.querySelectorAll('[data-bc-cart-drawer-close], [data-cart-drawer-close]');
    this.cartBadges = this.querySelectorAll('[data-bc-cart-count-badge], [data-cart-count-badge]');
    this.syncCartDrawerViewportHeight();
    if (!this.cartViewportListenersBound) {
      window.addEventListener('resize', this.handleCartViewportResize, { passive: true });
      window.visualViewport?.addEventListener('resize', this.handleCartViewportResize, { passive: true });
      window.visualViewport?.addEventListener('scroll', this.handleCartViewportResize, { passive: true });
      this.cartViewportListenersBound = true;
    }
    this.cartTriggers.forEach(trigger => {
      this.bindBuilderTap(trigger, () => {
        this.openCartDrawer({ userInitiated: true });
      });
    });
    cartCloseButtons.forEach(btn => {
      const isScrimClose = btn.classList.contains('cart-drawer-scrim');
      this.bindBuilderTap(btn, (event) => {
        if (isScrimClose && performance.now() < this.cartDrawerSuppressCloseUntil) {
          if (event?.cancelable) event.preventDefault();
          if (typeof event?.stopPropagation === 'function') event.stopPropagation();
          return;
        }
        const isTouchClose = event?.pointerType === 'touch' || event?.pointerType === 'pen';
        this.closeCartDrawer({ restoreFocus: !isTouchClose });
      }, {});
    });
    this.cartDrawerFooter?.addEventListener('click', async (e) => {
      const clearButton = e.target.closest('[data-cart-clear]');
      if (!clearButton) return;
      e.preventDefault();
      if (clearButton.disabled) return;
      const itemCount = Number(this.latestCart ? this.getOptimisticCart(this.latestCart).item_count : 0) || 0;
      if (itemCount <= 0) return;
      if (this.openCartClearConfirm(clearButton)) return;
      clearButton.disabled = true;
      clearButton.style.opacity = '0.45';
      try {
        await this.clearCart();
      } catch (error) {
        this.showCartDrawerNotice('Could not clear cart. Please try again.');
        clearButton.disabled = false;
        clearButton.style.opacity = '';
      }
    });
    this.cartClearConfirm?.addEventListener('click', async (e) => {
      const cancelButton = e.target.closest('[data-cart-clear-cancel]');
      const confirmButton = e.target.closest('[data-cart-clear-confirm-action]');
      if (e.target === this.cartClearConfirm || cancelButton) {
        e.preventDefault();
        this.closeCartClearConfirm();
        return;
      }
      if (!confirmButton) return;
      e.preventDefault();
      confirmButton.disabled = true;
      confirmButton.style.opacity = '0.45';
      this.closeCartClearConfirm({ restoreFocus: false, resetAction: false });
      try {
        await this.clearCart();
      } catch (error) {
        this.showCartDrawerNotice('Could not clear cart. Please try again.');
        confirmButton.disabled = false;
        confirmButton.style.opacity = '';
        if (this.cartClearButton) {
          this.cartClearButton.disabled = false;
          this.cartClearButton.style.opacity = '';
        }
      }
    });
    this.cartDrawerBody?.addEventListener('click', async (e) => {
      const qtyButton = e.target.closest('[data-cart-qty]');
      const removeButton = e.target.closest('[data-cart-remove]');
      const item = e.target.closest('[data-cart-item]');
      if (!item || (!qtyButton && !removeButton) || item.dataset.updating === 'true') return;
      e.preventDefault();
      const key = item.getAttribute('data-line-key');
      const variantId = item.getAttribute('data-variant-id') || '';
      const isOptimisticPending = item.getAttribute('data-optimistic') === 'true';
      const currentQty = Number(item.getAttribute('data-quantity')) || 0;
      const delta = qtyButton ? Number(qtyButton.getAttribute('data-cart-qty')) || 0 : 0;
      const maxQuantity = this.parseCartNumber(item.getAttribute('data-max-quantity'));
      if (!key || (qtyButton && qtyButton.disabled) || (removeButton && removeButton.disabled)) return;
      if (qtyButton && delta > 0 && maxQuantity !== null && currentQty >= maxQuantity) {
        qtyButton.disabled = true;
        this.showCartDrawerNotice(maxQuantity === 1 ? 'Only 1 available.' : `Only ${maxQuantity} available.`);
        return;
      }
      if (removeButton && isOptimisticPending && variantId) {
        this.pendingCartRemovals.add(key);
        item.style.display = 'none';
        if (this.latestCart) this.renderCart(this.latestCart);
        return;
      }
      item.dataset.updating = 'true';
      item.style.opacity = '0.4';
      item.style.pointerEvents = 'none';
      try {
        if (qtyButton) {
          const nextQty = Math.max(0, currentQty + delta);
          if (nextQty === 0) {
            this.pendingCartRemovals.add(key);
            item.style.display = 'none';
            if (this.latestCart) this.renderCart(this.latestCart);
          } else {
            item.setAttribute('data-quantity', String(nextQty));
            const qtyText = item.querySelector('.cart-drawer-qty span');
            if (qtyText) qtyText.textContent = String(nextQty);
          }
          await this.changeCartItem(key, nextQty);
        } else if (removeButton) {
          this.pendingCartRemovals.add(key);
          item.style.display = 'none';
          if (this.latestCart) this.renderCart(this.latestCart);
          await this.changeCartItem(key, 0);
        }
      } catch (error) {
        item.dataset.updating = 'false';
        item.style.display = '';
        item.style.opacity = '';
        item.style.pointerEvents = '';
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.cartClearConfirm && !this.cartClearConfirm.hidden) {
        this.closeCartClearConfirm();
        return;
      }
      if (e.key === 'Escape' && this.cartDrawer?.classList.contains('is-open')) {
        this.closeCartDrawer();
      }
    });
  }
  getCartEndpoint(path) {
    const root = this.rootUrl.endsWith('/') ? this.rootUrl : `${this.rootUrl}/`;
    return `${root}cart${path}`;
  }
  escapeHtml(value = '') {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
  formatMoney(cents = 0) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: this.fallbackCurrency,
        currencyDisplay: 'code'
      }).format(cents / 100);
    } catch (error) {
      return `${this.fallbackCurrency} ${(cents / 100).toFixed(2)}`;
    }
  }
  parseCartNumber(value) {
    if (value === null || value === undefined || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }
  normalizeCartMaxQuantity(value) {
    const number = this.parseCartNumber(value);
    return number === null ? null : Math.max(0, number);
  }
  getCartLineVariantId(item = {}) {
    const variantId = item.variant_id ?? item.variantId;
    return variantId === null || variantId === undefined || variantId === '' ? '' : String(variantId);
  }
  getPendingCartKeyForVariant(variantId) {
    const normalizedVariantId = variantId === null || variantId === undefined || variantId === '' ? '' : String(variantId);
    return normalizedVariantId ? `pending-${normalizedVariantId}` : '';
  }
  getCartLineKey(item = {}) {
    if (item.key !== null && item.key !== undefined && item.key !== '') return String(item.key);
    return this.getPendingCartKeyForVariant(this.getCartLineVariantId(item));
  }
  getCartItemOrderKeys(item = {}) {
    const keys = [];
    const itemKey = this.getCartLineKey(item);
    const variantId = this.getCartLineVariantId(item);
    if (itemKey) keys.push(`key:${itemKey}`);
    if (variantId) keys.push(`variant:${variantId}`);
    return keys;
  }
  rememberCartDrawerItemOrder(items = []) {
    const nextOrder = [];
    const seenKeys = new Set();
    items.forEach(item => {
      this.getCartItemOrderKeys(item).forEach(key => {
        if (seenKeys.has(key)) return;
        seenKeys.add(key);
        nextOrder.push(key);
      });
    });
    this.cartDrawerItemOrder = nextOrder;
  }
  getCartItemPreviousOrder(item = {}) {
    let previousOrder = Infinity;
    this.getCartItemOrderKeys(item).forEach(key => {
      const index = this.cartDrawerItemOrder.indexOf(key);
      if (index >= 0) previousOrder = Math.min(previousOrder, index);
    });
    return previousOrder;
  }
  stabilizeCartDrawerItemOrder(cart = {}) {
    if (!this.cartDrawerItemOrder.length || !Array.isArray(cart.items) || cart.items.some(item => item?.pending === true)) {
      return cart;
    }
    const sortedItems = cart.items
      .map((item, index) => ({
        item,
        index,
        previousOrder: this.getCartItemPreviousOrder(item)
      }))
      .sort((a, b) => {
        if (a.previousOrder !== b.previousOrder) return a.previousOrder - b.previousOrder;
        return a.index - b.index;
      })
      .map(entry => entry.item);
    const didReorder = sortedItems.some((item, index) => item !== cart.items[index]);
    return didReorder ? { ...cart, items: sortedItems } : cart;
  }
  getCartItemMaxQuantity(item = {}) {
    const inventoryManagement = item.inventory_management ?? item.inventoryManagement;
    const inventoryPolicy = item.inventory_policy ?? item.inventoryPolicy;
    const inventoryQuantity = this.parseCartNumber(item.inventory_quantity ?? item.inventoryQuantity);
    const quantityRuleMax = this.parseCartNumber(item.quantity_rule_max ?? item.quantityRuleMax ?? item.quantity_rule?.max ?? item.quantityRule?.max);
    let maxQuantity = quantityRuleMax;
    if (inventoryManagement && inventoryPolicy === 'deny' && inventoryQuantity !== null) {
      maxQuantity = maxQuantity === null ? inventoryQuantity : Math.min(maxQuantity, inventoryQuantity);
    }
    return this.normalizeCartMaxQuantity(maxQuantity);
  }
  renderCartItemMeta(item = {}) {
    if (item.options_with_values?.length) {
      return item.options_with_values
        .filter(option => option.value && option.value !== 'Default Title')
        .map(option => `${this.escapeHtml(option.name)}: ${this.escapeHtml(option.value)}`)
        .join('<br>');
    }
    if (item.variant_title && item.variant_title !== 'Default Title') {
      return this.escapeHtml(item.variant_title);
    }
    return '';
  }
  resetCartBodyScroll() {
    if (!this.cartDrawerBody) return;
    this.cartDrawerBody.scrollTop = 0;
    this.cartDrawerBody.scrollLeft = 0;
    window.requestAnimationFrame(() => {
      if (!this.cartDrawerBody) return;
      this.cartDrawerBody.scrollTop = 0;
      this.cartDrawerBody.scrollLeft = 0;
    });
  }
  restoreCartBodyScroll(top = 0, left = 0) {
    if (!this.cartDrawerBody) return;
    this.cartDrawerBody.scrollTop = top;
    this.cartDrawerBody.scrollLeft = left;
    window.requestAnimationFrame(() => {
      if (!this.cartDrawerBody) return;
      this.cartDrawerBody.scrollTop = top;
      this.cartDrawerBody.scrollLeft = left;
    });
  }
  showCartDrawerNotice(message) {
    if (!this.cartDrawerBody || !message) return;
    this.cartDrawerBody.querySelector('[data-cart-drawer-notice]')?.remove();
    const notice = document.createElement('p');
    notice.className = 'cart-drawer-notice';
    notice.setAttribute('data-cart-drawer-notice', '');
    notice.setAttribute('role', 'alert');
    notice.textContent = message;
    this.cartDrawerBody.prepend(notice);
    this.resetCartBodyScroll();
  }
  openCartClearConfirm(trigger) {
    if (!this.cartClearConfirm) return false;
    this.cartClearReturnFocus = trigger || document.activeElement;
    if (this.cartClearConfirmAction) {
      this.cartClearConfirmAction.disabled = false;
      this.cartClearConfirmAction.style.opacity = '';
    }
    this.cartClearConfirm.hidden = false;
    window.requestAnimationFrame(() => {
      this.cartClearConfirmAction?.focus({ preventScroll: true });
    });
    return true;
  }
  closeCartClearConfirm({ restoreFocus = true, resetAction = true } = {}) {
    if (!this.cartClearConfirm || this.cartClearConfirm.hidden) return;
    this.cartClearConfirm.hidden = true;
    if (resetAction && this.cartClearConfirmAction) {
      this.cartClearConfirmAction.disabled = false;
      this.cartClearConfirmAction.style.opacity = '';
    }
    const focusTarget = this.cartClearReturnFocus;
    this.cartClearReturnFocus = null;
    if (restoreFocus && focusTarget && typeof focusTarget.focus === 'function') {
      focusTarget.focus({ preventScroll: true });
    }
  }
  broadcastCartUpdate(cart, source = 'board-customizer') {
    if (!cart || typeof cart !== 'object') return;
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId: 'board-customizer',
        data: {
          source,
          itemCount: Number(cart.item_count) || 0
        }
      }
    }));
  }
  openLiveHeaderCartDrawer({ load = true, showLoading = true, force = false } = {}) {
    const headerCartDrawer = document.querySelector('.site-header #CartDrawer') || document.getElementById('CartDrawer');
    if (!headerCartDrawer) return false;

    const cart = this.latestCart ? this.getOptimisticCart(this.latestCart) : null;
    document.dispatchEvent(new CustomEvent('cart:open', {
      bubbles: true,
      detail: {
        cart,
        load,
        showLoading,
        force,
        source: 'board-customizer'
      }
    }));
    return true;
  }
  updateCartChrome(cart) {
    const count = cart?.item_count || 0;
    const label = count === 1 ? 'item' : 'items';
    const badgeText = count > 99 ? '99+' : String(count);
    this.cartTriggers = this.querySelectorAll('[data-bc-cart-drawer-trigger], [data-cart-drawer-trigger]');
    this.cartBadges = this.querySelectorAll('[data-bc-cart-count-badge], [data-cart-count-badge]');
    this.cartTriggers.forEach(trigger => {
      trigger.setAttribute('aria-label', `Open cart, ${count} ${label}`);
    });
    this.cartBadges.forEach(badge => {
      badge.textContent = badgeText;
      badge.classList.toggle('is-wide', badgeText.length > 1);
      badge.classList.toggle('is-hidden', count <= 0);
      badge.hidden = count <= 0;
      if (count > 0) {
        badge.style.display = 'inline-flex';
        badge.style.visibility = 'visible';
        badge.style.opacity = '1';
      } else {
        badge.style.removeProperty('display');
        badge.style.removeProperty('visibility');
        badge.style.removeProperty('opacity');
      }
    });
  }
  renderCart(cart, options = {}) {
    cart = this.stabilizeCartDrawerItemOrder(cart || {});
    this.latestCart = cart;
    const displayCart = this.getOptimisticCart(cart);
    this.updateCartChrome(displayCart);
    this.broadcastCartUpdate(displayCart, options.source || 'board-customizer');
    if (!this.cartDrawerBody || !this.cartDrawerFooter || !this.cartDrawerTotal) return;

    const visibleItems = displayCart.items || [];
    const shouldResetScroll = Boolean(options.resetScroll);
    const previousScrollTop = this.cartDrawerBody.scrollTop || 0;
    const previousScrollLeft = this.cartDrawerBody.scrollLeft || 0;
    this.cartDrawerTotal.textContent = this.formatMoney(displayCart.total_price);
    if (this.cartClearButton) {
      this.cartClearButton.disabled = false;
      this.cartClearButton.style.opacity = '';
    }
    if (visibleItems.length === 0) {
      this.rememberCartDrawerItemOrder([]);
      this.cartDrawerBody.innerHTML = '<p class="cart-drawer-empty">Your cart is empty. Add a deck, wheels, or fresh gear and it will show up here.</p>';
      this.cartDrawerFooter.hidden = true;
      if (shouldResetScroll) this.resetCartBodyScroll();
      else this.restoreCartBodyScroll(previousScrollTop, previousScrollLeft);
      return;
    }
    this.cartDrawerFooter.hidden = !visibleItems.some(item => item.pending !== true || item.optimistic === true);
    this.cartDrawerBody.innerHTML = `
      <ul class="cart-drawer-items">
        ${visibleItems.map(item => {
          const meta = this.renderCartItemMeta(item);
          const isPending = item.pending === true;
          const isOptimisticPending = isPending && item.optimistic === true;
          const quantity = Number(item.quantity) || 0;
          const itemKey = this.getCartLineKey(item);
          const variantId = this.getCartLineVariantId(item);
          const maxQuantity = this.getCartItemMaxQuantity(item);
          const maxQuantityAttr = maxQuantity !== null ? ` data-max-quantity="${maxQuantity}"` : '';
          const minusDisabled = quantity <= 1 || isPending ? ' disabled' : '';
          const isAtMaxQuantity = maxQuantity !== null && quantity >= maxQuantity;
          const plusDisabled = isAtMaxQuantity || isPending ? ' disabled' : '';
          const plusTitle = isAtMaxQuantity
            ? ` title="${this.escapeHtml(maxQuantity === 1 ? 'Only 1 available' : `Only ${maxQuantity} available`)}"`
            : '';
          const removeDisabled = isPending && !isOptimisticPending ? ' disabled aria-disabled="true"' : '';
          if (item.image) this.preloadDrawerImage(item.image);
          const image = item.image
            ? `<img src="${this.escapeHtml(item.image)}" alt="${this.escapeHtml(item.product_title || item.title)}" loading="lazy" decoding="async">`
            : '';
          return `
            <li class="cart-drawer-item${isPending ? ' is-pending' : ''}${isOptimisticPending ? ' is-optimistic' : ''}" data-cart-item data-line-key="${this.escapeHtml(itemKey)}" data-variant-id="${this.escapeHtml(variantId)}" data-pending="${isPending ? 'true' : 'false'}" data-optimistic="${isOptimisticPending ? 'true' : 'false'}" data-quantity="${quantity}"${maxQuantityAttr}>
              <a class="cart-drawer-item-media" href="${this.escapeHtml(item.url || '')}" tabindex="-1">${image}</a>
              <div>
                <h3 class="cart-drawer-item-title">
                  <a href="${this.escapeHtml(item.url || '')}">${this.escapeHtml(item.product_title || item.title)}</a>
                </h3>
                ${meta ? `<p class="cart-drawer-item-meta">${meta}</p>` : ''}
                ${isPending && !isOptimisticPending ? `
                  <div class="cart-drawer-item-bottom">
                    <span class="cart-drawer-pending">Adding...</span>
                  </div>
                ` : `
                  <div class="cart-drawer-item-bottom">
                    <div class="cart-drawer-qty" aria-label="Quantity controls">
                      <button type="button" data-cart-qty="-1" aria-label="Decrease quantity"${minusDisabled}>-</button>
                      <span>${quantity}</span>
                      <button type="button" data-cart-qty="1" aria-label="Increase quantity"${plusDisabled}${plusTitle}>+</button>
                    </div>
                    <span class="cart-drawer-price">${this.formatMoney(item.final_line_price)}</span>
                  </div>
                  <button class="cart-drawer-remove" type="button" data-cart-remove${removeDisabled}>Remove</button>
                `}
              </div>
            </li>
          `;
        }).join('')}
      </ul>
    `;
    this.rememberCartDrawerItemOrder(visibleItems);
    if (shouldResetScroll) this.resetCartBodyScroll();
    else this.restoreCartBodyScroll(previousScrollTop, previousScrollLeft);
  }
  async fetchCart() {
    const response = await fetch(this.getCartEndpoint('.js'), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });
    if (!response.ok) throw new Error('Could not load cart');
    return response.json();
  }
  isBuilderCartItem(item) {
    const properties = item?.properties || {};
    return Boolean(
      properties['Board Customizer Part'] ||
      properties.Service === 'Custom Board Assembly'
    );
  }
  getCartItemVariantId(item) {
    const variantId = Number.parseInt(item?.variant_id || item?.id, 10);
    return Number.isFinite(variantId) ? variantId : null;
  }
  getReplacementCartLines(cart, cartItems) {
    const selectedVariantIds = new Set(
      cartItems
        .map(entry => Number.parseInt(entry.item?.id, 10))
        .filter(Number.isFinite)
    );
    return (cart?.items || []).filter(item => {
      const variantId = this.getCartItemVariantId(item);
      return selectedVariantIds.has(variantId);
    });
  }
  async removeCartLinesByKey(lineKeys) {
    if (!lineKeys.length) return this.latestCart;
    const updates = lineKeys.reduce((map, key) => {
      map[key] = 0;
      return map;
    }, {});
    const response = await fetch(this.getCartEndpoint('/update.js'), {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
    if (!response.ok) throw new Error('Could not replace the existing build in cart');
    const cart = await response.json();
    this.pendingCartRemovals.clear();
    this.renderCart(cart);
    return cart;
  }
  async clearCart() {
    if (!this.cartDrawerBody || !this.cartDrawerFooter) return null;
    const clearRenderToken = ++this.cartClearRenderToken;
    let resolveCartClearWait = null;
    const cartClearWait = new Promise(resolve => {
      resolveCartClearWait = resolve;
    });
    const previousCart = this.latestCart ? {
      ...this.latestCart,
      items: Array.isArray(this.latestCart.items) ? this.latestCart.items.slice() : []
    } : null;
    const emptyCart = {
      ...(previousCart || {}),
      currency: previousCart?.currency || this.fallbackCurrency,
      item_count: 0,
      total_price: 0,
      items: []
    };
    this.cartActiveChanges += 1;
    this.cartLoadRequestId += 1;
    this.cartRefreshAfterChangeId += 1;
    window.HalfpipedCartClearPromise = cartClearWait;
    document.dispatchEvent(new CustomEvent('cart:clear:pending', { detail: { promise: cartClearWait } }));
    this.cartDrawer?.classList.add('is-updating');
    this.pendingCartRemovals.clear();
    this.renderCart(emptyCart, { resetScroll: true });
    try {
      const response = await fetch(this.getCartEndpoint('/clear.js'), {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error('Could not clear cart');
      const cart = await response.json();
      this.pendingCartRemovals.clear();
      if (clearRenderToken === this.cartClearRenderToken) {
        this.renderCart(cart, { resetScroll: true });
      }
      return cart;
    } catch (error) {
      if (previousCart && clearRenderToken === this.cartClearRenderToken) {
        this.renderCart(previousCart, { resetScroll: true });
      }
      this.showCartDrawerNotice('Could not clear cart. Please try again.');
      throw error;
    } finally {
      this.cartActiveChanges = Math.max(0, this.cartActiveChanges - 1);
      if (this.cartActiveChanges === 0) {
        this.cartDrawer?.classList.remove('is-updating');
      }
      if (window.HalfpipedCartClearPromise === cartClearWait) {
        window.HalfpipedCartClearPromise = null;
      }
      resolveCartClearWait?.();
      document.dispatchEvent(new CustomEvent('cart:clear:complete', { detail: { cart: this.latestCart } }));
      if (this.cartClearButton) {
        this.cartClearButton.disabled = false;
        this.cartClearButton.style.opacity = '';
        this.cartClearButton.blur?.();
      }
    }
  }
  getOptimisticCart(cart = {}) {
    const items = Array.isArray(cart.items) ? cart.items : [];
    if (!items.length || this.pendingCartRemovals.size === 0) {
      return {
        ...cart,
        items,
        item_count: Number(cart.item_count) || 0,
        total_price: Number(cart.total_price) || 0
      };
    }
    let removedItemCount = 0;
    let removedTotal = 0;
    const visibleItems = items.filter(item => {
      if (!this.pendingCartRemovals.has(this.getCartLineKey(item))) return true;
      removedItemCount += Number(item.quantity) || 0;
      removedTotal += Number(item.final_line_price) || 0;
      return false;
    });
    return {
      ...cart,
      items: visibleItems,
      item_count: Math.max(0, (Number(cart.item_count) || 0) - removedItemCount),
      total_price: Math.max(0, (Number(cart.total_price) || 0) - removedTotal)
    };
  }
  async changeCartItem(id, quantity) {
    this.cartActiveChanges += 1;
    this.cartDrawer?.classList.add('is-updating');
    const runChange = async () => {
      const response = await fetch(this.getCartEndpoint('/change.js'), {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, quantity })
      });
      if (!response.ok) throw new Error('Could not update cart');
      const newCart = await response.json();
      this.pendingCartRemovals.delete(id);
      this.renderCart(newCart);
      return newCart;
    };
    const queuedChange = this.cartChangeQueue.then(runChange, runChange);
    this.cartChangeQueue = queuedChange.catch(() => {});
    try {
      return await queuedChange;
    } catch (error) {
      this.pendingCartRemovals.delete(id);
      if (this.latestCart) this.renderCart(this.latestCart);
      if (this.cartDrawerBody) {
        this.cartDrawerBody.insertAdjacentHTML('afterbegin', '<p class="cart-drawer-empty">Could not update cart. Please try again.</p>');
      }
      throw error;
    } finally {
      this.cartActiveChanges = Math.max(0, this.cartActiveChanges - 1);
      if (this.cartActiveChanges === 0) {
        this.cartDrawer?.classList.remove('is-updating');
        this.refreshCartAfterChanges();
      }
    }
  }
  async refreshCartAfterChanges() {
    const requestId = ++this.cartRefreshAfterChangeId;
    try {
      const cart = await this.fetchCart();
      if (requestId !== this.cartRefreshAfterChangeId || this.cartActiveChanges !== 0) return;
      this.pendingCartRemovals.clear();
      this.cartLastFetchedAt = Date.now();
      this.renderCart(cart);
    } catch (error) {
    }
  }
  async loadAndRenderCart() {
    if (!this.cartDrawerBody) return;
    const requestId = ++this.cartLoadRequestId;
    this.cartDrawerBody.innerHTML = '<p class="cart-drawer-loading">Loading cart...</p>';
    if (this.cartDrawerFooter) this.cartDrawerFooter.hidden = true;
    this.resetCartBodyScroll();
    try {
      const cart = await this.fetchCart();
      if (requestId !== this.cartLoadRequestId) return;
      this.cartLastFetchedAt = Date.now();
      this.renderCart(cart, { resetScroll: true });
    } catch (error) {
      if (requestId !== this.cartLoadRequestId) return;
      this.cartDrawerBody.innerHTML = '<p class="cart-drawer-empty">Could not load cart. You can still view your cart page.</p>';
      if (this.cartDrawerFooter) this.cartDrawerFooter.hidden = true;
      this.resetCartBodyScroll();
    }
  }
  lockCartDrawerScroll() {
    if (this.cartDrawerScrollLocked) return;
    this.lockPageScroll({ fixedBody: false });
    this.cartDrawerScrollLocked = true;
  }
  unlockCartDrawerScroll() {
    if (!this.cartDrawerScrollLocked) return;
    this.cartDrawerScrollLocked = false;
    this.unlockPageScroll();
  }
  async openCartDrawer({ load = true, userInitiated = false, force = false, showLoading = true } = {}) {
    if (!this.cartDrawer) {
      if (load) {
        try {
          const cart = await this.fetchCart();
          this.cartLastFetchedAt = Date.now();
          this.renderCart(cart, { resetScroll: true });
        } catch (error) {
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
      }
      const hasRenderedCart = Boolean(this.latestCart);
      this.openLiveHeaderCartDrawer({
        load: !hasRenderedCart,
        showLoading: showLoading && !hasRenderedCart,
        force
      });
      return;
    }
    if (!force && performance.now() < this.cartDrawerSuppressOpenUntil) return;
    if (this.cartDrawerIsOpenOrOpening || this.cartDrawer.classList.contains('is-open')) {
      if (load) await this.loadAndRenderCart();
      return;
    }
    this.cartDrawerIsOpenOrOpening = true;
    this.lastFocusedElement = document.activeElement;
    this.cartDrawerSuppressCloseUntil = performance.now() + 650;
    window.clearTimeout(this.cartDrawerCloseTimer);
    this.cartDrawer.classList.remove('is-closing');
    this.cartDrawer.classList.add('is-open');
    document.body.classList.add('hp-cart-drawer-open');
    this.cartDrawer.setAttribute('aria-hidden', 'false');
    this.lockCartDrawerScroll();
    this.resetCartBodyScroll();
    this.cartDrawerPanel?.focus({ preventScroll: true });
    if (!load && showLoading && this.cartDrawerBody) {
      this.cartDrawerBody.innerHTML = '<p class="cart-drawer-loading">Adding to cart...</p>';
      if (this.cartDrawerFooter) this.cartDrawerFooter.hidden = true;
    }
    if (load) await this.loadAndRenderCart();
  }
  closeCartDrawer({ restoreFocus = true } = {}) {
    if (!this.cartDrawer) return;
    if (!this.cartDrawerIsOpenOrOpening && !this.cartDrawer.classList.contains('is-open')) return;
    this.cartDrawerIsOpenOrOpening = false;
    this.cartDrawerSuppressOpenUntil = performance.now() + 450;
    this.cartLoadRequestId += 1;
    this.cartRefreshAfterChangeId += 1;
    this.closeCartClearConfirm({ restoreFocus: false });
    const focusTarget = this.lastFocusedElement;
    this.lastFocusedElement = null;
    window.clearTimeout(this.cartDrawerCloseTimer);
    this.cartDrawer.classList.remove('is-open');
    this.cartDrawer.classList.add('is-closing');
    document.body.classList.remove('hp-cart-drawer-open');
    this.cartDrawer.setAttribute('aria-hidden', 'true');
    if (this.latestCart) this.updateCartChrome(this.getOptimisticCart(this.latestCart));
    this.cartDrawerItemOrder = [];
    this.unlockCartDrawerScroll();
    this.cartDrawerCloseTimer = window.setTimeout(() => {
      this.cartDrawer?.classList.remove('is-closing');
    }, 180);
    if (restoreFocus && focusTarget && typeof focusTarget.focus === 'function') {
      focusTarget.focus({ preventScroll: true });
    }
  }
  hydrateProductCardDisplayNames(grid) {
    if (!grid) return;
    grid.querySelectorAll('.bc-product-card').forEach(card => {
      const type = card.getAttribute('data-type');
      const item = this.getProductCardItem(card);
      const displayName = this.getItemDisplayName(item);
      if (displayName) {
        card.setAttribute('data-display-title', displayName);
        const nameEl = card.querySelector('.bc-product-card__name');
        if (nameEl) nameEl.textContent = displayName;
      }
      const sizeLabel = this.getProductCardSizeLabel(item, type);
      let sizeEl = card.querySelector('.bc-product-card__size');
      card.classList.toggle('has-size-label', Boolean(sizeLabel));
      if (!sizeLabel) {
        if (sizeEl) sizeEl.remove();
        return;
      }
      if (!sizeEl) {
        sizeEl = document.createElement('span');
        sizeEl.className = 'bc-product-card__size';
        const meta = card.querySelector('.bc-product-card__meta');
        const price = card.querySelector('.bc-product-card__price');
        if (meta) {
          meta.insertBefore(sizeEl, price || null);
        }
      }
      sizeEl.textContent = sizeLabel;
    });
  }
  getProductCardSizeLabel(item, type) {
    if (!item) return '';
    if (type === 'deck') {
      const width =
        this.normalizeWidthValue(item?.specs?.width) ||
        this.normalizeWidthValue(item?.selectedSize) ||
        this.extractWidthFromName(item?.variantTitle, false) ||
        this.extractWidthFromName(item?.name, false);
      return width ? this.formatSpecValue('width', width) : '';
    }
    if (type === 'truck') {
      const width =
        this.normalizeWidthValue(item?.specs?.width) ||
        this.normalizeWidthValue(item?.selectedSize) ||
        this.extractTruckWidthFromName(item?.variantTitle) ||
        this.extractTruckWidthFromName(item?.name);
      return width ? this.formatSpecValue('width', width) : '';
    }
    if (type === 'wheel') {
      const diameter =
        this.normalizeWheelDiameter(item?.specs?.diameter) ||
        this.normalizeWheelDiameter(item?.variantTitle) ||
        this.normalizeWheelDiameter(item?.name);
      const hardness =
        this.normalizeWheelHardness(item?.specs?.hardness) ||
        this.normalizeWheelHardness(item?.variantTitle) ||
        this.normalizeWheelHardness(item?.name);
      return [diameter, hardness].filter(Boolean).join(' / ');
    }
    return '';
  }
  initCollectionDrawers() {
    this.querySelectorAll('[data-open-drawer]').forEach(trigger => {
      trigger.addEventListener('click', () => this.openDrawer(trigger.getAttribute('data-open-drawer')));
    });
    this.querySelectorAll('[data-close-drawer]').forEach(trigger => {
      trigger.addEventListener('click', (event) => {
        const drawer = event.target.closest('.bc-drawer');
        if (drawer) this.closeDrawer(drawer);
      });
    });
    const preloadFromEvent = event => {
      const card = event.target.closest('.bc-product-card');
      if (card) this.preloadCardTexture(card);
      const imageWrapper = event.target.closest('.bc-product-card__image-wrapper');
      if (imageWrapper) {
        this.preloadDrawerImage(imageWrapper.getAttribute('data-full-image'));
      }
    };
    this.addEventListener('pointermove', event => {
      const card = event.target.closest?.('.bc-product-card');
      this.setHoveredProductCard(card || null);
    }, { passive: true });
    this.addEventListener('mouseover', event => {
      const card = event.target.closest?.('.bc-product-card');
      this.setHoveredProductCard(card || null);
    });
    this.addEventListener('pointerleave', () => this.clearHoveredProductCard());
    this.addEventListener('mouseleave', () => this.clearHoveredProductCard());
    this.querySelectorAll('.bc-drawer__grid').forEach(grid => {
      this.hydrateProductCardDisplayNames(grid);
      grid.addEventListener('pointerdown', preloadFromEvent, { passive: true });
      grid.addEventListener('pointerover', preloadFromEvent);
      grid.addEventListener('focusin', preloadFromEvent);
      grid.addEventListener('touchstart', preloadFromEvent, { passive: true });
      grid.addEventListener('pointerover', event => {
        const card = event.target.closest('.bc-product-card');
        if (card && grid.contains(card)) this.setHoveredProductCard(card);
      });
      grid.addEventListener('pointerout', event => {
        const card = event.target.closest('.bc-product-card');
        if (!card || !grid.contains(card) || card.contains(event.relatedTarget)) return;
        this.clearHoveredProductCard();
      });
      grid.addEventListener('focusin', event => {
        const card = event.target.closest('.bc-product-card');
        if (card && grid.contains(card)) this.setHoveredProductCard(card);
      });
      grid.addEventListener('focusout', event => {
        const card = event.target.closest('.bc-product-card');
        if (!card || !grid.contains(card) || card.contains(event.relatedTarget)) return;
        this.clearHoveredProductCard();
      });
      grid.addEventListener('click', event => {
        const card = event.target.closest('.bc-product-card');
        if (!card || !grid.contains(card)) return;
        const type = card.getAttribute('data-type');
        const zoomTrigger = event.target.closest('.bc-product-card__zoom');
        const imageWrapper = event.target.closest('.bc-product-card__image-wrapper');
        if (zoomTrigger && imageWrapper && card.contains(imageWrapper)) {
          const img = imageWrapper.querySelector('img');
          if (img) {
            const item = this.getProductCardItem(card);
            event.preventDefault();
            event.stopPropagation();
            this.clearHoveredProductCard();
            this.openImageModal(
              imageWrapper.getAttribute('data-full-image') || img.currentSrc || img.src,
              this.getItemDisplayName(item) || card.getAttribute('data-display-title') || card.getAttribute('data-title') || img.alt || 'Part Image',
              img.currentSrc || img.src,
              item?.description || '',
              item?.descriptionHtml || '',
              item?.images || []
            );
            return;
          }
        }
        const item = { ...this.getProductCardItem(card) };
        item.imageUrl = item.imageUrl || (card.querySelector('img') ? card.querySelector('img').src : null);
        item.fullImageUrl = item.fullImageUrl || card.querySelector('.bc-product-card__image-wrapper')?.getAttribute('data-full-image') || item.imageUrl;
        const didSelect = this.handleOptionClick(null, type, item);
        if (didSelect === false) return;
        const drawer = card.closest('.bc-drawer');
        if (drawer) this.closeDrawer(drawer);
      });
    });
  }
  getCatalogItems(type) {
    const map = {
      deck: this.decks,
      grip: this.grips,
      wheel: this.wheels,
      bearing: this.bearings,
      truck: this.trucks,
      hardware: this.hardware
    };
    return map[type] || [];
  }
  findCatalogItem(type, uid) {
    return this.getCatalogItems(type).find(item => item.uid === uid) || null;
  }
  getProductCardItem(card) {
    const type = card?.getAttribute('data-type');
    if (!card || !type) return null;
    const image = card.querySelector('img');
    const imageWrapper = card.querySelector('.bc-product-card__image-wrapper');
    const cardImages = this.parseImageList(imageWrapper?.getAttribute('data-images'));
    const catalogItem = this.findCatalogItem(type, card.getAttribute('data-uid'));
    if (catalogItem) {
      return {
        ...catalogItem,
        imageUrl: catalogItem.imageUrl || (image ? (image.currentSrc || image.src) : null),
        fullImageUrl: catalogItem.fullImageUrl || imageWrapper?.getAttribute('data-full-image') || null,
        images: catalogItem.images?.length ? catalogItem.images : cardImages
      };
    }
    return this.enrichFilterItem({
      uid: card.getAttribute('data-uid'),
      name: card.getAttribute('data-title'),
      productTitle: card.getAttribute('data-product-title'),
      variantTitle: card.getAttribute('data-variant-title'),
      texture: card.getAttribute('data-texture'),
      color: card.getAttribute('data-color'),
      baseplateColor: card.getAttribute('data-baseplate-color'),
      bushingTopColor: card.getAttribute('data-bushing-top-color'),
      bushingBottomColor: card.getAttribute('data-bushing-bottom-color'),
      washerTopColor: card.getAttribute('data-washer-top-color'),
      washerBottomColor: card.getAttribute('data-washer-bottom-color'),
      axleNutColor: card.getAttribute('data-axle-nut-color'),
      kingpinNutColor: card.getAttribute('data-kingpin-nut-color'),
      variantId: card.getAttribute('data-variant-id'),
      price: card.getAttribute('data-price'),
      description: card.getAttribute('data-description'),
      descriptionHtml: card.getAttribute('data-description-html'),
      tags: this.normalizeTagList(card.getAttribute('data-tags')),
      specs: {
        width: card.getAttribute('data-spec-width'),
        fitRange: card.getAttribute('data-fit-range'),
        diameter: card.getAttribute('data-spec-diameter'),
        hardness: card.getAttribute('data-spec-hardness')
      },
      imageUrl: image ? (image.currentSrc || image.src) : null,
      fullImageUrl: imageWrapper?.getAttribute('data-full-image') || null,
      images: cardImages,
      isCollection: true
    }, type);
  }
  getFilterConfig(type) {
    if (type === 'deck') return [{ field: 'width', label: 'Width' }];
    if (type === 'truck') return [{ field: 'width', label: 'Width' }];
    if (type === 'wheel') return [
      { field: 'diameter', label: 'Diameter' },
      { field: 'hardness', label: 'Hardness' }
    ];
    return [];
  }
  formatSpecValue(field, value) {
    if (field === 'width') {
      const label = this.normalizeWidthValue(value);
      if (!label) return value;
      return `${label}"`;
    }
    return value;
  }
  sortSpecValues(field, values) {
    return values.sort((a, b) => {
      const aNum = Number.parseFloat(a);
      const bNum = Number.parseFloat(b);
      if (Number.isFinite(aNum) && Number.isFinite(bNum)) return aNum - bNum;
      return String(a).localeCompare(String(b), undefined, { numeric: true });
    });
  }
  getItemWidth(item) {
    return this.normalizeWidthValue(item?.specs?.width);
  }
  getTruckFitRange(item) {
    return this.parseDeckFitRange(item?.specs?.fitRange || item?.fitRange);
  }
  isChosenSelection(item) {
    return Boolean(item && !item.isPlaceholder);
  }
  getFitReference(type) {
    if (type === 'truck' && this.isChosenSelection(this.selectedDeck)) {
      const width = this.getItemWidth(this.selectedDeck);
      return width ? {
        width,
        sourceType: 'deck',
        targetType: 'trucks',
        sourceName: this.selectedDeck.name
      } : null;
    }
    return null;
  }
  widthsFit(deckWidth, truckWidth) {
    const deck = Number.parseFloat(deckWidth);
    const truck = Number.parseFloat(truckWidth);
    if (!Number.isFinite(deck) || !Number.isFinite(truck)) return false;
    return Math.abs(deck - truck) <= 0.13 + 0.0001;
  }
  truckFitsDeck(deckWidth, truck) {
    const deck = Number.parseFloat(deckWidth);
    if (!Number.isFinite(deck)) return false;
    const fitRange = this.getTruckFitRange(truck);
    if (fitRange) {
      return deck >= fitRange.min - 0.0001 && deck <= fitRange.max + 0.0001;
    }
    const truckWidth = this.getItemWidth(truck);
    return Boolean(truckWidth && this.widthsFit(deckWidth, truckWidth));
  }
  deckFitsSelectedTruck(deck) {
    if (!this.isChosenSelection(this.selectedTruck)) return true;
    const deckWidth = this.getItemWidth(deck);
    return Boolean(deckWidth && this.truckFitsDeck(deckWidth, this.selectedTruck));
  }
  clearTruckSelection() {
    this.selectedTruck = this.createEmptySelection('truck');
    this.removeUrlParam('truck');
    this.updateTruckColor(this.selectedTruck.color, this.selectedTruck.baseplateColor, this.selectedTruck);
  }
  itemFitsSelectedCounterpart(type, item) {
    const reference = this.getFitReference(type);
    if (!reference) return true;
    if (type === 'truck') return this.truckFitsDeck(reference.width, item);
    return true;
  }
  ensureFitNote(type, grid) {
    if (type !== 'deck' && type !== 'truck') return null;
    const drawer = this.querySelector(`#bc-drawer-${type}`);
    let note = drawer?.querySelector('.bc-fit-note');
    if (!note && grid) {
      note = document.createElement('p');
      note.className = 'bc-fit-note';
      note.setAttribute('data-fit-note', type);
      note.hidden = true;
      grid.before(note);
    }
    return note || null;
  }
  updateFitNote(type, reference) {
    const drawer = this.querySelector(`#bc-drawer-${type}`);
    const note = drawer?.querySelector('.bc-fit-note');
    if (!note) return;
    if (!reference) {
      note.hidden = true;
      note.textContent = '';
      return;
    }
    const widthLabel = this.formatSpecValue('width', reference.width);
    const changeTarget = reference.sourceType === 'deck' ? 'deck' : 'trucks';
    note.textContent = `Based on your ${widthLabel} ${reference.sourceType}, these are the ${reference.targetType} that fit. Change your ${changeTarget} to see more designs.`;
    note.hidden = false;
  }
  isFilterOptionAllowed(type, field, value) {
    if (!value || value === 'all') return true;
    const reference = this.getFitReference(type);
    if (type === 'truck' && field === 'width' && reference) {
      const width = this.normalizeWidthValue(value);
      const matchingTrucks = this.getCatalogItems('truck').filter(item => this.getItemWidth(item) === width);
      return matchingTrucks.some(item => this.truckFitsDeck(reference.width, item)) || this.widthsFit(reference.width, width);
    }
    return true;
  }
  updateFilterOptionAvailability(type) {
    const drawer = this.querySelector(`#bc-drawer-${type}`);
    if (!drawer) return;
    drawer.querySelectorAll('.bc-filter-select[data-filter-field]').forEach(select => {
      const field = select.getAttribute('data-filter-field');
      const selectedValue = select.value || 'all';
      let selectedWasDisabled = false;
      if (!select._bcFilterOptions) {
        select._bcFilterOptions = Array.from(select.options).map(option => ({
          value: option.value,
          label: option.textContent || option.value
        }));
      }
      select.innerHTML = '';
      select._bcFilterOptions.forEach(optionData => {
        const allowed = this.isFilterOptionAllowed(type, field, optionData.value);
        if (!allowed) {
          if (optionData.value === selectedValue) selectedWasDisabled = true;
          return;
        }
        const option = new Option(optionData.label, optionData.value);
        select.add(option);
      });
      const hasSelectedValue = Array.from(select.options).some(option => option.value === selectedValue);
      if (hasSelectedValue && !selectedWasDisabled) {
        select.value = selectedValue;
      } else {
        select.value = 'all';
        if (this.activeDrawerFilters[type]) {
          this.activeDrawerFilters[type][field] = 'all';
        }
      }
    });
  }
  initDrawerFilters() {
    ['deck', 'grip', 'wheel', 'bearing', 'truck', 'hardware'].forEach(type => {
      const drawer = this.querySelector(`#bc-drawer-${type}`);
      const grid = drawer?.querySelector('.bc-drawer__grid');
      if (!drawer || !grid) return;
      const groups = (['deck', 'wheel', 'truck'].includes(type) ? this.getFilterConfig(type) : [])
        .map(group => {
          const options = [...new Set(this.getCatalogItems(type).flatMap(item => this.getItemFilterValues(item, group.field, type)).filter(Boolean))];
          return { ...group, options: this.sortSpecValues(group.field, options) };
        })
        .filter(group => group.options.length > 1);
      this.activeDrawerFilters[type] = groups.reduce((filters, group) => {
        filters[group.field] = 'all';
        return filters;
      }, {});
      this.activeDrawerSort[type] = 'manual';
      const panel = document.createElement('div');
      panel.className = 'bc-filter-panel';
      panel.setAttribute('data-filter-panel', type);
      panel.innerHTML = `
        <div class="bc-filter-group" data-sort-group>
          <label class="bc-filter-label" for="bc-sort-${this.escapeHtml(type)}">Sort</label>
          <span class="bc-filter-select-wrap">
            <select class="bc-filter-select" id="bc-sort-${this.escapeHtml(type)}" data-sort-select aria-label="${this.escapeHtml(`${type} sort`)}">
              <option value="manual">Featured</option>
              <option value="price-asc">Price low to high</option>
              <option value="price-desc">Price high to low</option>
            </select>
          </span>
        </div>
        ${groups.map(group => `
        <div class="bc-filter-group" data-filter-group="${this.escapeHtml(group.field)}">
          <label class="bc-filter-label" for="bc-filter-${this.escapeHtml(type)}-${this.escapeHtml(group.field)}">${this.escapeHtml(group.label)}</label>
          <span class="bc-filter-select-wrap">
            <select class="bc-filter-select" id="bc-filter-${this.escapeHtml(type)}-${this.escapeHtml(group.field)}" data-filter-field="${this.escapeHtml(group.field)}" aria-label="${this.escapeHtml(`${group.label} filter`)}">
              <option value="all">All ${this.escapeHtml(group.label)}</option>
              ${group.options.map(value => `
                <option value="${this.escapeHtml(value)}">${this.escapeHtml(this.formatSpecValue(group.field, value))}</option>
              `).join('')}
            </select>
          </span>
        </div>
      `).join('')}
        <div class="bc-filter-group bc-filter-group--reset">
          <button type="button" class="bc-filter-reset" data-filter-reset>Reset filters</button>
        </div>`;
      const empty = document.createElement('p');
      empty.className = 'bc-filter-empty';
      empty.textContent = 'No parts match these filters.';
      grid.before(panel);
      this.ensureFitNote(type, grid);
      grid.after(empty);
      panel.addEventListener('click', event => {
        const resetButton = event.target.closest('[data-filter-reset]');
        if (!resetButton) return;
        this.activeDrawerSort[type] = 'manual';
        Object.keys(this.activeDrawerFilters[type] || {}).forEach(field => {
          this.activeDrawerFilters[type][field] = 'all';
        });
        const sortSelect = panel.querySelector('[data-sort-select]');
        if (sortSelect) sortSelect.value = 'manual';
        panel.querySelectorAll('[data-filter-field]').forEach(select => {
          select.value = 'all';
        });
        this.sortDrawerCards(type);
        this.applyDrawerFilters(type);
        resetButton.classList.add('is-pressed');
        window.setTimeout(() => {
          resetButton.classList.remove('is-pressed');
          resetButton.blur?.();
        }, 180);
      });
      panel.addEventListener('change', event => {
        const sortSelect = event.target.closest('[data-sort-select]');
        if (sortSelect) {
          this.activeDrawerSort[type] = sortSelect.value || 'manual';
          this.sortDrawerCards(type);
          this.applyDrawerFilters(type);
          return;
        }
        const select = event.target.closest('.bc-filter-select');
        if (!select) return;
        const field = select.getAttribute('data-filter-field');
        if (!field) return;
        const value = select.value || 'all';
        this.activeDrawerFilters[type][field] = value;
        this.applyDrawerFilters(type);
      });
    });
  }
  getCardPriceCents(card) {
    const explicitPrice = Number.parseInt(card.getAttribute('data-price-cents') || '', 10);
    if (Number.isFinite(explicitPrice)) return explicitPrice;
    const priceText = card.getAttribute('data-price') || '';
    const parsed = Number.parseFloat(priceText.replace(/[^0-9.]/g, ''));
    return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
  }
  sortDrawerCards(type) {
    const drawer = this.querySelector(`#bc-drawer-${type}`);
    const grid = drawer?.querySelector('.bc-drawer__grid');
    if (!grid) return;
    const sortMode = this.activeDrawerSort[type] || 'manual';
    const cards = Array.from(grid.querySelectorAll(`.bc-product-card[data-type="${type}"]`));
    cards.forEach((card, index) => {
      if (!card.hasAttribute('data-sort-index')) card.setAttribute('data-sort-index', String(index));
    });
    cards.sort((a, b) => {
      if (sortMode === 'price-asc') return this.getCardPriceCents(a) - this.getCardPriceCents(b);
      if (sortMode === 'price-desc') return this.getCardPriceCents(b) - this.getCardPriceCents(a);
      return Number(a.getAttribute('data-sort-index') || 0) - Number(b.getAttribute('data-sort-index') || 0);
    });
    cards.forEach(card => grid.appendChild(card));
  }
  applyDrawerFilters(type) {
    const drawer = this.querySelector(`#bc-drawer-${type}`);
    if (!drawer) return;
    this.sortDrawerCards(type);
    this.updateFilterOptionAvailability(type);
    const filters = this.activeDrawerFilters[type] || {};
    const fitReference = this.getFitReference(type);
    let visibleCount = 0;
    drawer.querySelectorAll(`.bc-product-card[data-type="${type}"]`).forEach(card => {
      const item = this.getProductCardItem(card);
      const matchesFilters = Object.entries(filters).every(([field, value]) => {
        if (!value || value === 'all') return true;
        return this.getItemFilterValues(item, field, type).includes(value);
      });
      const matchesFit = this.itemFitsSelectedCounterpart(type, item);
      const matches = matchesFilters && matchesFit;
      card.classList.toggle('is-filtered-out', !matches);
      if (matches) visibleCount += 1;
    });
    this.updateFitNote(type, fitReference);
    drawer.querySelector('.bc-filter-empty')?.classList.toggle('is-visible', visibleCount === 0);
    this.primeDrawerImages(type);
  }
  openDrawer(type) {
    const drawer = this.querySelector(`#bc-drawer-${type}`);
    if (!drawer) {
      alert(`Add a ${type} collection in this section setting first.`);
      return;
    }
    drawer.classList.add('is-open');
    this.updateActiveProductCards(type);
    this.applyDrawerFilters(type);
    this.primeDrawerImages(type);
    window.setTimeout(() => this.preloadDrawerTextures(type), 260);
  }
  closeDrawer(drawer) {
    drawer.classList.remove('is-open');
    const type = drawer.id?.replace('bc-drawer-', '');
    const observer = type ? this.drawerImageObservers.get(type) : null;
    if (observer) {
      observer.disconnect();
      this.drawerImageObservers.delete(type);
    }
  }
  updateActiveProductCards(type = null) {
    const selectedByType = {
      deck: this.selectedDeck,
      grip: this.selectedGrip,
      wheel: this.selectedWheel,
      bearing: this.selectedBearing,
      truck: this.selectedTruck,
      hardware: this.selectedHardware
    };
    const selector = type ? `.bc-product-card[data-type="${type}"]` : '.bc-product-card';
    this.querySelectorAll(selector).forEach(card => {
      const cardType = card.getAttribute('data-type');
      const selected = selectedByType[cardType];
      const cardUid = card.getAttribute('data-uid');
      const isActive = selected && (cardUid === selected.uid || (cardType === 'deck' && cardUid === selected.productUid));
      card.classList.toggle('is-active', isActive);
      card.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      const chooseLabel = card.querySelector('.bc-product-card__choose');
      if (chooseLabel) chooseLabel.textContent = isActive ? 'Selected' : 'Choose part';
    });
  }
  handleOptionClick(e, type, item) {
    if (type === 'deck') {
      item = this.resolveDeckVariantSelection(item);
      if (!item) return false;
      this.selectedDeck = item;
      this.updateUrlParam('deck', item.uid);
      if (!this.deckFitsSelectedTruck(item)) {
        this.clearTruckSelection();
      }
      this.updateDeckTexture(item.texture);
      this.animateCamera(new THREE.Vector3(2.25, 1.05, 0), new THREE.Vector3(0, 0.3, 0));
    } else if (type === 'grip') {
      this.selectedGrip = item;
      this.updateUrlParam('grip', item.uid);
      this.updateGripTexture(item.texture);
      this.updateGripColor(item.color);
      this.animateCamera(new THREE.Vector3(-2.472, 0.84, 0.336), new THREE.Vector3(0, 0.18, 0));
    } else if (type === 'wheel') {
      this.selectedWheel = item;
      this.updateUrlParam('wheel', item.uid);
      this.updateWheelTexture(item.texture);
      this.spinWheels();
      this.animateCamera(new THREE.Vector3(0.09, 1, 0.9), new THREE.Vector3(-0.08, 0.54, 0.64));
    } else if (type === 'bearing') {
      this.selectedBearing = item;
      this.updateUrlParam('bearing', item.uid);
      this.updateBearingColor(item.color);
      this.animateCamera(new THREE.Vector3(0.005, 0.77, 0.77), new THREE.Vector3(-0.08, 0.54, 0.64));
    } else if (type === 'truck') {
      this.selectedTruck = item;
      this.updateUrlParam('truck', item.uid);
      this.updateTruckColor(item.color, item.baseplateColor, item);
      this.animateCamera(new THREE.Vector3(0.21, 0.23, 1.06), new THREE.Vector3(-0.12, 0.29, 0.57));
    } else if (type === 'hardware') {
      this.selectedHardware = item;
      this.updateUrlParam('hardware', item.uid);
      this.updateHardwareColors(item);
      this.animateCamera(new THREE.Vector3(-0.62, 0.38, 0.89), new THREE.Vector3(-0.25, 0.3, 0.62));
    }
    this.renderUI();
    return true;
  }
  updateUrlParam(key, value) {
    const url = new URL(window.location.href);
    url.searchParams.set(key, value);
    window.history.replaceState({}, '', url.href);
  }
  removeUrlParam(key) {
    const url = new URL(window.location.href);
    url.searchParams.delete(key);
    window.history.replaceState({}, '', url.href);
  }
  initThree() {
    const container = this.querySelector('#bc-canvas-container');
    const width = container.clientWidth;
    const height = container.clientHeight;
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color('#3B3A3A'); 
    this.scene.fog = new THREE.Fog('#3B3A3A', 3, 10);
    this.camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 100);
    this.camera.position.copy(this.defaultCameraPosition);
    this.renderer = new THREE.WebGLRenderer({ 
      antialias: true, 
      alpha: true,
      powerPreference: "high-performance"
    });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.renderer.domElement.style.touchAction = 'none';
    this.renderer.domElement.style.userSelect = 'none';
    container.appendChild(this.renderer.domElement);
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.minDistance = 0.035;
    this.controls.maxDistance = 6; // Allowed zooming out further
    this.controls.maxPolarAngle = Math.PI / 2;
    this.controls.target.copy(this.defaultCameraTarget);
    this.controls.update();
    this.controls.addEventListener('start', () => {
      getMotion().killTweensOf(this.camera.position);
      getMotion().killTweensOf(this.controls.target);
    });
    this.initCanvasPointerGuards();
    this.initPhaseThroughZoom();
    const ambientLight = new THREE.AmbientLight('#ffffff', 0.8);
    this.scene.add(ambientLight);
    const directionalLight = new THREE.DirectionalLight('#ffffff', 1.6);
    directionalLight.position.set(1, 1, 1);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 1024;
    directionalLight.shadow.mapSize.height = 1024;
    directionalLight.shadow.bias = -0.001;
    this.scene.add(directionalLight);
    const floorGeo = new THREE.PlaneGeometry(6, 6);
    const floorMat = new THREE.MeshBasicMaterial({ visible: false });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    this.scene.add(floor);
    this.buildStageFloor();
    const animate = () => {
      if (!this.isConnected || !this.renderer || !this.scene || !this.camera || !this.controls) return;
      this.animationFrameId = requestAnimationFrame(animate);
      this.controls.update();
      this.renderer.render(this.scene, this.camera);
    };
    animate();
  }
  buildStageFloor() {
    this.textureLoader.load('https://cdn.shopify.com/s/files/1/0767/1719/5522/files/metal-normal.avif?v=1777292849', (normalMap) => {
      normalMap.wrapS = THREE.RepeatWrapping;
      normalMap.wrapT = THREE.RepeatWrapping;
      normalMap.repeat.set(30, 30);
      normalMap.anisotropy = 8;
      const floorMat = new THREE.MeshStandardMaterial({
        roughness: 0.75,
        color: '#3B3A3A',
        normalMap: normalMap
      });
      const circleGeo = new THREE.CircleGeometry(20, 32);
      const stageFloor = new THREE.Mesh(circleGeo, floorMat);
      stageFloor.position.set(0, -0.005, 0);
      stageFloor.rotation.x = -Math.PI / 2;
      stageFloor.receiveShadow = true;
      stageFloor.castShadow = true;
      this.scene.add(stageFloor);
    });
  }
  loadAssets() {
    const draco = new DRACOLoader().setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
    const loader = new GLTFLoader();
    loader.setDRACOLoader(draco);
    loader.load(this.modelUrl, (gltf) => {
      const model = gltf.scene;
      model.rotation.set(0, 0, Math.PI / 2);
      model.position.set(0, 0.295, 0);
      model.traverse(child => {
        if (!child.isMesh) return;
        child.castShadow = true;
        child.receiveShadow = true;
        const name = child.name.toLowerCase();
        if (name === 'griptape') {
          this.gripTapeMesh = child;
          this.gripMaterial = new THREE.MeshStandardMaterial({
            bumpScale: 3.5,
            roughness: 0.82,
            color: this.selectedGrip.color || this.defaultGripColor || '#555555'
          });
          child.material = this.gripMaterial;
          this.textureLoader.load('https://cdn.shopify.com/s/files/1/0767/1719/5522/files/griptape-roughness.webp?v=1777292849', (roughness) => {
            roughness.wrapS = roughness.wrapT = THREE.RepeatWrapping;
            roughness.repeat.set(9, 9);
            roughness.anisotropy = 8;
            roughness.needsUpdate = true;
            this.gripMaterial.bumpMap = roughness;
            this.gripMaterial.roughnessMap = roughness;
            this.gripMaterial.needsUpdate = true;
          });
          this.updateGripTexture(this.selectedGrip.texture);
          this.updateGripColor(this.selectedGrip.color);
        } else if (name === 'deck') {
          this.deckMesh = child;
          this.deckMaterial = new THREE.MeshStandardMaterial({
            roughness: 0.1,
            side: THREE.DoubleSide
          });
          this.deckMesh.material = this.deckMaterial;
          this.updateDeckTexture(this.selectedDeck.texture);
        } else if (name.includes('wheel')) {
          this.wheelRefs.push(child);
          if (!this.wheelMaterial) {
            this.wheelMaterial = new THREE.MeshStandardMaterial({ roughness: 0.35 });
          }
          child.material = this.wheelMaterial;
          this.addBearingCaps(child);
        } else if (name.includes('bearing')) {
          this.bearingRefs.push(child);
          if (!this.bearingMaterial) {
            this.bearingMaterial = new THREE.MeshStandardMaterial({ metalness: 0.85, roughness: 0.22 });
          }
          child.material = this.bearingMaterial;
          this.updateBearingColor(this.selectedBearing.color);
        } else if (name.includes('bushing')) {
          const isBottom = name.includes('bottom') || name.includes('lower');
          const materialKey = isBottom ? 'truckBushingBottomMaterial' : 'truckBushingTopMaterial';
          if (!this[materialKey]) {
            this[materialKey] = new THREE.MeshStandardMaterial({ metalness: 0.04, roughness: 0.55 });
          }
          child.material = this[materialKey];
        } else if (name.includes('washer')) {
          const isBottom = name.includes('bottom') || name.includes('lower');
          const materialKey = isBottom ? 'truckWasherBottomMaterial' : 'truckWasherTopMaterial';
          if (!this[materialKey]) {
            this[materialKey] = new THREE.MeshStandardMaterial({ metalness: 0.82, roughness: 0.24 });
          }
          child.material = this[materialKey];
        } else if (name.includes('truck') || name.includes('baseplate')) {
          if (name.includes('truck1')) this.truck1Mesh = child;
          if (name.includes('truck2')) this.truck2Mesh = child;
          if (name.includes('baseplate')) this.baseplatesMesh = child;
          if (!this.truckHangerMaterial) {
            this.truckHangerMaterial = new THREE.MeshStandardMaterial({ metalness: 0.8, roughness: 0.25 });
          }
          if (!this.truckBaseplateMaterial) {
            this.truckBaseplateMaterial = new THREE.MeshStandardMaterial({ metalness: 0.75, roughness: 0.28 });
          }
          if (!this.truckHangerMaterial.normalMap || !this.truckBaseplateMaterial.normalMap) {
            this.textureLoader.load('https://cdn.shopify.com/s/files/1/0767/1719/5522/files/metal-normal.avif?v=1777292849', (normal) => {
              normal.wrapS = normal.wrapT = THREE.RepeatWrapping;
              normal.repeat.set(8, 8);
              normal.anisotropy = 8;
              [this.truckHangerMaterial, this.truckBaseplateMaterial].forEach(material => {
                if (!material) return;
                material.normalMap = normal;
                material.normalScale.set(0.3, 0.3);
                material.needsUpdate = true;
              });
            });
          }
          child.material = name.includes('baseplate') ? this.truckBaseplateMaterial : this.truckHangerMaterial;
        } else if (name === 'bolts') {
          this.boltsMesh = child;
          this.boltMaterial = new THREE.MeshStandardMaterial({ metalness: 0.5, roughness: 0.3 });
          this.axleNutMaterial = new THREE.MeshStandardMaterial({ color: new THREE.Color('#050505'), metalness: 0.9, roughness: 0.22 });
          this.kingpinNutMaterial = new THREE.MeshStandardMaterial({ color: new THREE.Color('#050505'), metalness: 0.9, roughness: 0.22 });
          this.applyBoltMaterialGroups(this.boltsMesh);
          this.updateHardwareColors(this.selectedHardware);
        }
      });
      this.updateGripTexture(this.selectedGrip.texture);
      this.updateGripColor(this.selectedGrip.color);
      this.updateWheelTexture(this.selectedWheel.texture);
      this.updateBearingColor(this.selectedBearing.color);
      this.updateTruckColor(this.selectedTruck.color, this.selectedTruck.baseplateColor, this.selectedTruck);
      this.updateHardwareColors(this.selectedHardware);
      this.scene.add(model);
      const preloader = this.querySelector('#bc-preloader');
      if (preloader) preloader.classList.add('is-hidden');
    });
  }
  addBearingCaps(wheelMesh) {
    if (!wheelMesh) return;
    if (!this.bearingMaterial) {
      this.bearingMaterial = new THREE.MeshStandardMaterial({ metalness: 0.85, roughness: 0.22 });
    }
    if (!this.bearingCapGeometry) {
      this.bearingCapGeometry = new THREE.CylinderGeometry(0.028, 0.028, 0.012, 32);
    }
    [-1, 1].forEach(side => {
      const cap = new THREE.Mesh(this.bearingCapGeometry, this.bearingMaterial);
      cap.name = 'GeneratedBearingCap';
      cap.position.x = side * 0.032;
      cap.rotation.z = Math.PI / 2;
      cap.castShadow = true;
      cap.receiveShadow = true;
      wheelMesh.add(cap);
      this.bearingRefs.push(cap);
    });
    this.updateBearingColor(this.selectedBearing.color);
  }
  preloadCardTexture(card) {
    const type = card.getAttribute('data-type');
    if (type !== 'deck' && type !== 'grip' && type !== 'wheel') return;
    this.preloadTexture(card.getAttribute('data-texture'));
  }
  preloadDrawerTextures(type) {
    if (type !== 'deck' && type !== 'grip' && type !== 'wheel') return;
    const drawer = this.querySelector(`#bc-drawer-${type}`);
    if (!drawer?.classList.contains('is-open')) return;
    const cards = Array.from(drawer.querySelectorAll(`.bc-product-card[data-type="${type}"]`))
      .filter(card => !card.classList.contains('is-filtered-out'));
    const priorityCount = type === 'deck' ? 10 : type === 'wheel' ? 8 : 8;
    cards.forEach((card, index) => {
      const load = () => this.preloadCardTexture(card);
      const delay = index * (type === 'wheel' ? 150 : 125);
      if (index < priorityCount) {
        window.setTimeout(load, delay);
        return;
      }
      const idleLoad = () => window.setTimeout(load, (index - priorityCount) * 180);
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(idleLoad, { timeout: 3500 + (index - priorityCount) * 160 });
      } else {
        window.setTimeout(idleLoad, 2200);
      }
    });
  }
  preloadGripTextures() {
    this.grips.filter(grip => grip?.texture).forEach((grip, index) => {
      const load = () => this.preloadTexture(grip.texture);
      if (index < 6) {
        window.setTimeout(load, index * 180);
        return;
      }
      const idleLoad = () => window.setTimeout(load, (index - 6) * 220);
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(idleLoad, { timeout: 4200 + (index - 6) * 180 });
      } else {
        window.setTimeout(idleLoad, 2600);
      }
    });
  }
  preloadTexture(url) {
    if (!url) return Promise.resolve(null);
    return this.loadTexture(url).catch(error => {
      console.error('Board customizer failed to preload texture:', url, error);
      return null;
    });
  }
  prepareTexture(tex) {
    tex.flipY = false;
    tex.colorSpace = THREE.SRGBColorSpace;
    if (this.renderer) {
      tex.anisotropy = Math.min(this.renderer.capabilities.getMaxAnisotropy(), 8);
    }
    tex.needsUpdate = true;
    return tex;
  }
  loadTexture(url) {
    if (!url) return Promise.reject(new Error('Missing texture URL'));
    if (this.textureCache[url]) return Promise.resolve(this.textureCache[url]);
    if (this.texturePromises[url]) return this.texturePromises[url];
    this.texturePromises[url] = new Promise((resolve, reject) => {
      this.textureLoader.load(url, (tex) => {
        this.prepareTexture(tex);
        this.renderer?.initTexture?.(tex);
        this.textureCache[url] = tex;
        delete this.texturePromises[url];
        resolve(tex);
      }, undefined, (error) => {
        delete this.texturePromises[url];
        reject(error);
      });
    });
    return this.texturePromises[url];
  }
  updateDeckTexture(url) {
    if (!url || !this.deckMaterial) return;
    const requestId = ++this.deckTextureRequestId;
    this.loadTexture(url).then((tex) => {
      if (requestId !== this.deckTextureRequestId || !this.deckMaterial) return;
      this.deckMaterial.map = tex;
      this.deckMaterial.needsUpdate = true;
    }).catch((error) => console.error('Failed to load deck texture:', error));
  }
  updateGripTexture(url) {
    if (!url || !this.gripMaterial) return;
    const requestId = ++this.gripTextureRequestId;
    this.loadTexture(url).then((tex) => {
      if (requestId !== this.gripTextureRequestId || !this.gripMaterial) return;
      const usesDefaultGripTexture = this.defaultGripUrl && url === this.defaultGripUrl;
      tex.wrapS = usesDefaultGripTexture ? THREE.RepeatWrapping : THREE.ClampToEdgeWrapping;
      tex.wrapT = usesDefaultGripTexture ? THREE.RepeatWrapping : THREE.ClampToEdgeWrapping;
      tex.repeat.set(usesDefaultGripTexture ? 9 : 1, usesDefaultGripTexture ? 9 : 1);
      tex.offset.set(0, 0);
      tex.needsUpdate = true;
      this.gripMaterial.map = tex;
      this.gripMaterial.needsUpdate = true;
    }).catch((error) => console.error('Failed to load grip texture:', error));
  }
  updateWheelTexture(url) {
    const targetUrl = String(url || '').trim();
    const requestId = ++this.wheelTextureRequestId;
    if (!this.wheelMaterial) return;
    if (!targetUrl) {
      this.wheelMaterial.map = null;
      this.wheelMaterial.needsUpdate = true;
      return;
    }
    this.loadTexture(targetUrl).then((tex) => {
      if (requestId !== this.wheelTextureRequestId || !this.wheelMaterial) return;
      this.wheelMaterial.map = tex;
      this.wheelMaterial.needsUpdate = true;
    }).catch((error) => console.error('Failed to load wheel texture:', error));
  }
  normalizeColorValue(colorValue) {
    const color = String(colorValue || '').trim().toLowerCase();
    const namedColors = {
      'light blue': '#7DD3FC',
      lightblue: '#7DD3FC',
      'sky blue': '#7DD3FC',
      skyblue: '#7DD3FC',
      blue: '#1D4ED8',
      black: '#111111',
      gold: '#D4A72C',
      silver: '#C9C9C9',
      white: '#FFFFFF',
      red: '#C62828',
      green: '#15803D',
      yellow: '#FACC15',
      purple: '#7E22CE',
      orange: '#F97316'
    };
    return namedColors[color] || colorValue;
  }
  applyTruckPaint(material, colorHex, metalness, roughness) {
    if (!material || !colorHex) return;
    const color = new THREE.Color(this.normalizeColorValue(colorHex));
    const hsl = {};
    color.getHSL(hsl);
    const isWhitePaint = hsl.l > 0.86 && hsl.s < 0.18;
    material.color.copy(color);
    material.metalness = isWhitePaint ? 0.02 : metalness;
    material.roughness = isWhitePaint ? 0.42 : roughness;
    if (isWhitePaint) {
      material.emissive.copy(color);
      material.emissiveIntensity = 0.08;
    } else {
      material.emissive.set('#000000');
      material.emissiveIntensity = 0;
    }
    material.needsUpdate = true;
  }
  updateTruckColor(hangerColorHex, baseplateColorHex = hangerColorHex, parts = {}) {
    this.applyTruckPaint(this.truckHangerMaterial, hangerColorHex, 0.8, 0.25);
    this.applyTruckPaint(this.truckBaseplateMaterial, baseplateColorHex, 0.75, 0.28);
    this.applyTruckPaint(this.truckBushingTopMaterial, parts.bushingTopColor || parts.bushingColor || '#111111', 0.04, 0.55);
    this.applyTruckPaint(this.truckBushingBottomMaterial, parts.bushingBottomColor || parts.bushingTopColor || '#111111', 0.04, 0.55);
    this.applyTruckPaint(this.truckWasherTopMaterial, parts.washerTopColor || baseplateColorHex, 0.82, 0.24);
    this.applyTruckPaint(this.truckWasherBottomMaterial, parts.washerBottomColor || parts.washerTopColor || baseplateColorHex, 0.82, 0.24);
  }
  updateGripColor(colorHex) {
    if (!this.gripMaterial || !colorHex) return;
    const hasCustomGripTexture = this.selectedGrip?.texture && this.defaultGripUrl && this.selectedGrip.texture !== this.defaultGripUrl;
    this.gripMaterial.color.set(hasCustomGripTexture ? '#ffffff' : this.normalizeColorValue(colorHex));
    this.gripMaterial.needsUpdate = true;
  }
  updateBearingColor(colorHex) {
    if (!this.bearingMaterial || !colorHex) return;
    this.applyTruckPaint(this.bearingMaterial, colorHex, 0.85, 0.22);
  }
  applyBoltMaterialGroups(mesh) {
    if (!mesh?.geometry || !this.boltMaterial) return;
    if (!this.axleNutMaterial) {
      this.axleNutMaterial = new THREE.MeshStandardMaterial({ color: new THREE.Color('#050505'), metalness: 0.9, roughness: 0.22 });
    }
    if (!this.kingpinNutMaterial) {
      this.kingpinNutMaterial = new THREE.MeshStandardMaterial({ color: new THREE.Color('#050505'), metalness: 0.9, roughness: 0.22 });
    }
    const geometry = mesh.geometry;
    const position = geometry.attributes.position;
    if (!position) {
      mesh.material = this.boltMaterial;
      return;
    }
    if (geometry.userData.bcHardwareSplit) {
      mesh.material = [this.boltMaterial, this.axleNutMaterial, this.kingpinNutMaterial];
      return;
    }
    const index = geometry.index;
    const boltIndices = [];
    const axleNutIndices = [];
    const kingpinNutIndices = [];
    const triangleCount = Math.floor((index ? index.count : position.count) / 3);
    const nutThresholdY = -0.02;
    const axleNutThresholdX = 0.18;
    const getIndex = index
      ? (offset) => index.getX(offset)
      : (offset) => offset;
    for (let tri = 0; tri < triangleCount; tri += 1) {
      const a = getIndex(tri * 3);
      const b = getIndex(tri * 3 + 1);
      const c = getIndex(tri * 3 + 2);
      const centerX = (position.getX(a) + position.getX(b) + position.getX(c)) / 3;
      const centerY = (position.getY(a) + position.getY(b) + position.getY(c)) / 3;
      let target = boltIndices;
      if (centerY < nutThresholdY) {
        target = Math.abs(centerX) > axleNutThresholdX ? axleNutIndices : kingpinNutIndices;
      }
      target.push(a, b, c);
    }
    if (!boltIndices.length || (!axleNutIndices.length && !kingpinNutIndices.length)) {
      mesh.material = this.boltMaterial;
      return;
    }
    const groups = [
      { indices: boltIndices, materialIndex: 0 },
      { indices: axleNutIndices, materialIndex: 1 },
      { indices: kingpinNutIndices, materialIndex: 2 }
    ].filter(group => group.indices.length > 0);
    geometry.setIndex(groups.flatMap(group => group.indices));
    geometry.clearGroups();
    let start = 0;
    groups.forEach(group => {
      geometry.addGroup(start, group.indices.length, group.materialIndex);
      start += group.indices.length;
    });
    geometry.computeBoundingSphere();
    geometry.userData.bcHardwareSplit = true;
    mesh.material = [this.boltMaterial, this.axleNutMaterial, this.kingpinNutMaterial];
  }
  paintHardwareMaterial(material, colorHex) {
    if (material && colorHex) {
      const color = new THREE.Color(this.normalizeColorValue(colorHex));
      const hsl = {};
      color.getHSL(hsl);
      if (hsl.l <= 0.15) {
        material.metalness = 0.9;
        material.roughness = 0.2;
        color.setHSL(hsl.h, hsl.s, 0.15); // Lighten pure black slightly for specularity
      } else {
        material.metalness = 0.5;
        material.roughness = 0.3;
      }
      material.color.copy(color);
      material.needsUpdate = true;
    }
  }
  updateHardwareColors(hardware) {
    const colors = typeof hardware === 'object' && hardware
      ? hardware
      : { color: hardware };
    this.paintHardwareMaterial(this.boltMaterial, colors.color);
    this.paintHardwareMaterial(this.axleNutMaterial, colors.axleNutColor || '#050505');
    this.paintHardwareMaterial(this.kingpinNutMaterial, colors.kingpinNutColor || '#050505');
  }
  updateHardwareColor(colorHex) {
    this.updateHardwareColors({ color: colorHex });
  }
  updateBoltColor(colorHex) {
    this.updateHardwareColor(colorHex);
  }
  initCanvasPointerGuards() {
    if (!this.renderer || !this.controls) return;
    const canvas = this.renderer.domElement;
    const getPointerIds = (event) => {
      const pointerIds = new Set(this.activeCanvasPointerIds || []);
      if (this.activeCanvasPointerId != null) pointerIds.add(this.activeCanvasPointerId);
      if (event?.pointerId != null) pointerIds.add(event.pointerId);
      return [...pointerIds].filter(id => id != null);
    };
    const forwardOrbitControlsPointerEnd = (event, pointerIds) => {
      if (!pointerIds.length || !canvas.isConnected || this.isForwardingCanvasPointerEnd) return;
      const pointerType = event?.pointerType || (event?.type?.startsWith?.('touch') ? 'touch' : 'mouse');
      this.isForwardingCanvasPointerEnd = true;
      pointerIds.forEach(pointerId => {
        try {
          canvas.dispatchEvent(new PointerEvent('pointerup', {
            bubbles: true,
            cancelable: true,
            pointerId,
            pointerType,
            isPrimary: event?.isPrimary ?? pointerId === pointerIds[0],
            button: 0,
            buttons: 0,
            clientX: event?.clientX ?? 0,
            clientY: event?.clientY ?? 0
          }));
        } catch (pointerError) {
          canvas.dispatchEvent(new MouseEvent('mouseup', {
            bubbles: true,
            cancelable: true,
            button: 0,
            buttons: 0,
            clientX: event?.clientX ?? 0,
            clientY: event?.clientY ?? 0
          }));
        }
      });
      this.isForwardingCanvasPointerEnd = false;
    };
    const releaseCanvasDrag = (event) => {
      if (this.isForwardingCanvasPointerEnd || this.isReleasingCanvasDrag) return;
      const pointerIds = getPointerIds(event);
      if (!this.isCanvasDragging && this.activeCanvasPointerId === null && !pointerIds.length) return;
      this.isReleasingCanvasDrag = true;
      try {
        forwardOrbitControlsPointerEnd(event, pointerIds);
        if (event?.pointerId != null && canvas.hasPointerCapture?.(event.pointerId)) {
          try {
            canvas.releasePointerCapture(event.pointerId);
          } catch (error) {}
        }
        pointerIds.forEach(pointerId => {
          if (!canvas.hasPointerCapture?.(pointerId)) return;
          try {
            canvas.releasePointerCapture(pointerId);
          } catch (error) {}
        });
        this.activeCanvasPointerIds?.clear?.();
        this.activeCanvasPointerId = null;
        this.isCanvasDragging = false;
        this.controls.enabled = false;
        requestAnimationFrame(() => {
          if (!this.controls) return;
          this.controls.enabled = true;
          this.controls.update();
        });
      } finally {
        this.isReleasingCanvasDrag = false;
      }
    };
    const releaseIfIdle = (event) => {
      if (event?.buttons === 0 || event?.buttons == null) releaseCanvasDrag(event);
    };
    canvas.addEventListener('pointerdown', (event) => {
      this.activeCanvasPointerId = event.pointerId;
      this.activeCanvasPointerIds.add(event.pointerId);
      this.isCanvasDragging = true;
      if (canvas.setPointerCapture) {
        try {
          canvas.setPointerCapture(event.pointerId);
        } catch (error) {}
      }
    }, { capture: true });
    canvas.addEventListener('mousedown', () => {
      this.isCanvasDragging = true;
    }, { capture: true });
    canvas.addEventListener('lostpointercapture', (event) => {
      if (this.isForwardingCanvasPointerEnd) return;
      releaseCanvasDrag(event);
    }, { capture: true });
    canvas.addEventListener('pointerenter', releaseIfIdle, { capture: true });
    canvas.addEventListener('mouseenter', releaseIfIdle, { capture: true });
    canvas.addEventListener('pointerup', releaseCanvasDrag, { capture: true });
    canvas.addEventListener('pointercancel', releaseCanvasDrag, { capture: true });
    canvas.addEventListener('touchend', releaseCanvasDrag, { capture: true, passive: true });
    canvas.addEventListener('touchcancel', releaseCanvasDrag, { capture: true, passive: true });
    canvas.addEventListener('pointerleave', releaseIfIdle, { capture: true });
    canvas.addEventListener('pointermove', (event) => {
      if (this.activeCanvasPointerId === event.pointerId && event.buttons === 0) releaseCanvasDrag(event);
    }, { capture: true });
    window.addEventListener('pointerup', (event) => {
      if (this.activeCanvasPointerId === event.pointerId) releaseCanvasDrag(event);
    }, { capture: true });
    window.addEventListener('pointercancel', releaseCanvasDrag, { capture: true });
    window.addEventListener('mouseup', releaseCanvasDrag, { capture: true });
    window.addEventListener('touchend', releaseCanvasDrag, { capture: true, passive: true });
    window.addEventListener('touchcancel', releaseCanvasDrag, { capture: true, passive: true });
    window.addEventListener('blur', releaseCanvasDrag, { capture: true });
    document.addEventListener('pointerup', releaseCanvasDrag, { capture: true });
    document.addEventListener('pointercancel', releaseCanvasDrag, { capture: true });
    document.addEventListener('mouseup', releaseCanvasDrag, { capture: true });
    document.addEventListener('touchend', releaseCanvasDrag, { capture: true, passive: true });
    document.addEventListener('touchcancel', releaseCanvasDrag, { capture: true, passive: true });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) releaseCanvasDrag();
    }, { capture: true });
  }
  initPhaseThroughZoom() {
    if (!this.renderer || !this.camera || !this.controls) return;
    const canvas = this.renderer.domElement;
    canvas.addEventListener('wheel', (event) => {
      if (event.deltaY >= 0) return;
      const distance = this.camera.position.distanceTo(this.controls.target);
      if (distance > this.controls.minDistance + 0.045) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const direction = new THREE.Vector3();
      this.camera.getWorldDirection(direction);
      const step = Math.min(0.28, Math.max(0.035, Math.abs(event.deltaY) * 0.0018));
      this.camera.position.addScaledVector(direction, step);
      this.controls.target.addScaledVector(direction, step);
      this.controls.update();
    }, { passive: false, capture: true });
  }
  resetCamera() {
    this.animateCamera(this.defaultCameraPosition.clone(), this.defaultCameraTarget.clone());
  }
  animateCamera(targetPos, targetLookAt, duration = 0.85) {
    if (!this.camera || !this.controls) return;
    const motion = getMotion();
    motion.killTweensOf(this.camera.position);
    motion.killTweensOf(this.controls.target);
    this.camera.up.set(0, 1, 0);
    const updateControls = () => {
      this.camera.up.set(0, 1, 0);
      this.controls.update();
    };
    motion.to(this.camera.position, {
      x: targetPos.x,
      y: targetPos.y,
      z: targetPos.z,
      duration,
      ease: 'power2.out',
      overwrite: true,
      onUpdate: updateControls,
      onComplete: updateControls
    });
    motion.to(this.controls.target, {
      x: targetLookAt.x,
      y: targetLookAt.y,
      z: targetLookAt.z,
      duration,
      ease: 'power2.out',
      overwrite: true,
      onUpdate: updateControls,
      onComplete: updateControls
    });
  }
  spinWheels() {
    if (!this.wheelRefs.length) return;
    const motion = getMotion();
    this.wheelRefs.forEach(wheel => {
      motion.killTweensOf(wheel.rotation);
      motion.to(wheel.rotation, { x: '-=30', duration: 2.5, ease: 'circ.out' });
    });
  }
  updateDesktopScale() {
    const width = Math.max(1, window.innerWidth || document.documentElement.clientWidth || 390);
    const height = Math.max(1, window.innerHeight || document.documentElement.clientHeight || 844);
    const isDesktop = window.matchMedia('(min-width: 1024px) and (orientation: landscape) and (hover: hover) and (pointer: fine)').matches;
    const isTablet = !isDesktop && width >= 750;
    const profile = isDesktop ? 'desktop' : isTablet ? 'tablet' : 'mobile';
    const basis = {
      desktop: { width: 1920, height: 1080, min: 0.5, max: 1.12 },
      tablet: { width: 1024, height: 768, min: 0.62, max: 1.08 },
      mobile: { width: 390, height: 844, min: 0.72, max: 1.08 }
    }[profile];
    const scale = Math.max(basis.min, Math.min(basis.max, Math.min(width / basis.width, height / basis.height)));
    const setPx = (name, value) => {
      this.style.setProperty(name, `${(value * scale).toFixed(2)}px`);
    };
    const bases = {
      desktop: {
        headerPadY: 36, headerPadX: 52, logo: 152, menu: 46, cart: 92, badge: 32, badgeFont: 16, badgeOffset: -10,
        panelPadX: 34, panelPadY: 28, panelGap: 14, title: 44,
        headGapY: 7, headGapX: 12, headPadB: 13, kickerFont: 13, kickerPadY: 5, kickerPadX: 11,
        progressFont: 13, progressPadY: 5, progressPadX: 10, optionsGap: 13,
        partPadX: 17, partPadY: 15, partGap: 7, partBorderLeft: 8, partRadius: 8, partIndex: 31, partLabel: 28, partLabelPad: 54,
        copyFont: 15, statusMin: 92, statusFont: 14, statusPadY: 6, statusPadX: 10, statusRadius: 7,
        ctaHeight: 58, ctaFont: 20, disclaimerFont: 12, disclaimerMaxH: 32,
        controlSide: 42, controlBottom: 38, controlHeight: 64, controlPadX: 22, controlGap: 14, controlRadius: 11, controlFont: 22, countFont: 16,
        controlIcon: 22, chevron: 10, chevronBorder: 3, summaryWidth: 520, resetWidth: 330,
        rowHeight: 76, rowPadY: 12, rowPadX: 18, rowRadius: 10, rowLabelFont: 19, chooseMin: 104, chooseHeight: 42, chooseFont: 14,
        assemblyHeight: 72, assemblyPadY: 12, assemblyPadX: 18
      },
      tablet: {
        headerPadY: 26, headerPadX: 34, logo: 132, menu: 42, cart: 76, badge: 28, badgeFont: 14, badgeOffset: -8,
        panelPadX: 24, panelPadY: 22, panelGap: 12, title: 36,
        headGapY: 7, headGapX: 10, headPadB: 12, kickerFont: 12, kickerPadY: 4, kickerPadX: 10,
        progressFont: 12, progressPadY: 5, progressPadX: 9, optionsGap: 11,
        partPadX: 14, partPadY: 13, partGap: 6, partBorderLeft: 7, partRadius: 8, partIndex: 27, partLabel: 23, partLabelPad: 46,
        copyFont: 14, statusMin: 84, statusFont: 13, statusPadY: 5, statusPadX: 9, statusRadius: 7,
        ctaHeight: 54, ctaFont: 18, disclaimerFont: 11, disclaimerMaxH: 30,
        controlSide: 28, controlBottom: 28, controlHeight: 56, controlPadX: 18, controlGap: 12, controlRadius: 10, controlFont: 18, countFont: 15,
        controlIcon: 18, chevron: 9, chevronBorder: 3, summaryWidth: 390, resetWidth: 260,
        rowHeight: 70, rowPadY: 11, rowPadX: 16, rowRadius: 9, rowLabelFont: 17, chooseMin: 96, chooseHeight: 38, chooseFont: 13,
        assemblyHeight: 66, assemblyPadY: 11, assemblyPadX: 16
      },
      mobile: {
        headerPadY: 18, headerPadX: 18, logo: 96, menu: 36, cart: 58, badge: 24, badgeFont: 12, badgeOffset: -6,
        panelPadX: 14, panelPadY: 14, panelGap: 8, title: 24,
        headGapY: 5, headGapX: 8, headPadB: 8, kickerFont: 10, kickerPadY: 3, kickerPadX: 8,
        progressFont: 10, progressPadY: 4, progressPadX: 8, optionsGap: 8,
        partPadX: 10, partPadY: 9, partGap: 5, partBorderLeft: 5, partRadius: 7, partIndex: 22, partLabel: 17, partLabelPad: 34,
        copyFont: 12, statusMin: 68, statusFont: 11, statusPadY: 4, statusPadX: 8, statusRadius: 6,
        ctaHeight: 44, ctaFont: 15, disclaimerFont: 10, disclaimerMaxH: 24,
        controlSide: 16, controlBottom: 16, controlHeight: 46, controlPadX: 12, controlGap: 8, controlRadius: 9, controlFont: 13, countFont: 12,
        controlIcon: 13, chevron: 8, chevronBorder: 2.4, summaryWidth: 220, resetWidth: 188,
        rowHeight: 56, rowPadY: 9, rowPadX: 12, rowRadius: 8, rowLabelFont: 14, chooseMin: 78, chooseHeight: 32, chooseFont: 11,
        assemblyHeight: 52, assemblyPadY: 9, assemblyPadX: 12
      }
    };
    const b = bases[profile];
    const scaledVars = {
      '--bc-header-pad-y': b.headerPadY,
      '--bc-header-pad-x': b.headerPadX,
      '--bc-header-logo-w': b.logo,
      '--bc-menu-icon': b.menu,
      '--bc-cart-button': b.cart,
      '--bc-cart-badge': b.badge,
      '--bc-cart-badge-offset': b.badgeOffset,
      '--bc-cart-badge-font': b.badgeFont,
      '--bc-panel-pad-x': b.panelPadX,
      '--bc-panel-pad-y': b.panelPadY,
      '--bc-panel-gap': b.panelGap,
      '--bc-builder-head-gap-y': b.headGapY,
      '--bc-builder-head-gap-x': b.headGapX,
      '--bc-builder-head-pad-b': b.headPadB,
      '--bc-builder-kicker-font': b.kickerFont,
      '--bc-builder-kicker-pad-y': b.kickerPadY,
      '--bc-builder-kicker-pad-x': b.kickerPadX,
      '--bc-title-font': b.title,
      '--bc-progress-font': b.progressFont,
      '--bc-progress-pad-y': b.progressPadY,
      '--bc-progress-pad-x': b.progressPadX,
      '--bc-options-gap': b.optionsGap,
      '--bc-part-pad-x': b.partPadX,
      '--bc-part-pad-y': b.partPadY,
      '--bc-scaled-part-pad-x': b.partPadX,
      '--bc-scaled-part-pad-y': b.partPadY,
      '--bc-part-gap': b.partGap,
      '--bc-part-border-left': b.partBorderLeft,
      '--bc-part-radius': b.partRadius,
      '--bc-part-index-font': b.partIndex,
      '--bc-part-label-font': b.partLabel,
      '--bc-part-label-pad': b.partLabelPad,
      '--bc-copy-font': b.copyFont,
      '--bc-status-min': b.statusMin,
      '--bc-status-font': b.statusFont,
      '--bc-status-pad-y': b.statusPadY,
      '--bc-status-pad-x': b.statusPadX,
      '--bc-status-radius': b.statusRadius,
      '--bc-cta-height': b.ctaHeight,
      '--bc-cta-font': b.ctaFont,
      '--bc-disclaimer-font': b.disclaimerFont,
      '--bc-disclaimer-max-h': b.disclaimerMaxH,
      '--bc-control-side': b.controlSide,
      '--bc-control-bottom': b.controlBottom,
      '--bc-control-height': b.controlHeight,
      '--bc-control-pad-x': b.controlPadX,
      '--bc-control-gap': b.controlGap,
      '--bc-control-radius': b.controlRadius,
      '--bc-control-font': b.controlFont,
      '--bc-control-count-font': b.countFont,
      '--bc-control-icon': b.controlIcon,
      '--bc-chevron-size': b.chevron,
      '--bc-chevron-border': b.chevronBorder,
      '--bc-summary-width': b.summaryWidth,
      '--bc-reset-width': b.resetWidth,
      '--bc-row-height': b.rowHeight,
      '--bc-row-pad-y': b.rowPadY,
      '--bc-row-pad-x': b.rowPadX,
      '--bc-row-radius': b.rowRadius,
      '--bc-row-label-font': b.rowLabelFont,
      '--bc-choose-min': b.chooseMin,
      '--bc-choose-height': b.chooseHeight,
      '--bc-choose-font': b.chooseFont,
      '--bc-assembly-height': b.assemblyHeight,
      '--bc-assembly-pad-y': b.assemblyPadY,
      '--bc-assembly-pad-x': b.assemblyPadX
    };
    this.style.setProperty('--bc-scale', scale.toFixed(3));
    this.style.setProperty('--bc-desktop-scale', scale.toFixed(3));
    this.style.setProperty('--bc-profile', profile);
    this.style.setProperty('--bc-copy-lines', profile === 'desktop' && height >= 760 && width >= 1180 ? '3' : '2');
    Object.entries(scaledVars).forEach(([name, value]) => setPx(name, value));
    this.style.setProperty('--bc-count-pad-y', `${Math.max(2, b.statusPadY * scale * 0.62).toFixed(2)}px`);
    this.style.setProperty('--bc-count-pad-x', `${Math.max(6, b.statusPadX * scale * 1.05).toFixed(2)}px`);
    this.style.setProperty('--bc-part-border', `${Math.max(1, 2 * scale).toFixed(2)}px`);
    this.style.setProperty('--bc-tool-border', `${Math.max(1, 2 * scale).toFixed(2)}px`);
    this.classList.toggle('is-desktop-scaled', isDesktop);
    this.classList.toggle('is-tablet-scaled', profile === 'tablet');
    this.classList.toggle('is-mobile-scaled', profile === 'mobile');
  }
  handleResize() {
    this.updateDesktopScale();
    const container = this.querySelector('#bc-canvas-container');
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const width = Math.max(1, rect.width || container.clientWidth || 1);
    const height = Math.max(1, rect.height || container.clientHeight || 1);
    if (this.camera && this.renderer) {
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(width, height);
    }
  }
  async readCartAddError(response) {
    try {
      const payload = await response.clone().json();
      return payload.description || payload.message || payload.status || `Shopify returned ${response.status}`;
    } catch (jsonError) {
      try {
        const text = await response.text();
        return text || `Shopify returned ${response.status}`;
      } catch (textError) {
        return `Shopify returned ${response.status}`;
      }
    }
  }
  async handleAddToCart() {
    if (this.isAddingToCart) return;
    const selectedParts = [
      { type: 'Deck', key: 'deck', selection: this.selectedDeck, options: this.decks },
      { type: 'Grip', key: 'grip', selection: this.selectedGrip, options: this.grips },
      { type: 'Wheels', key: 'wheel', selection: this.selectedWheel, options: this.wheels },
      { type: 'Bearings', key: 'bearing', selection: this.selectedBearing, options: this.bearings },
      { type: 'Trucks', key: 'truck', selection: this.selectedTruck, options: this.trucks },
      { type: 'Hardware', key: 'hardware', selection: this.selectedHardware, options: this.hardware }
    ];
    const missingParts = selectedParts
      .filter(part => this.querySelector(`#bc-drawer-${part.key}`) && (!part.selection || part.selection.isPlaceholder))
      .map(part => part.type);
    if (missingParts.length > 0) {
      alert(`Choose these parts first: ${missingParts.join(', ')}.`);
      return;
    }
    const unavailableParts = selectedParts
      .filter(part => this.querySelector(`#bc-drawer-${part.key}`) && part.selection && !part.selection.isPlaceholder)
      .filter(part => !Number.isFinite(Number.parseInt(part.selection.variantId, 10)))
      .map(part => `${part.type}: ${this.getItemDisplayName(part.selection) || 'Unavailable item'}`);
    if (unavailableParts.length > 0) {
      alert(`These selections are unavailable right now: ${unavailableParts.join(', ')}.`);
      return;
    }
    const assemblyControl = this.querySelector('#bc-assembly');
    const wantsAssembly = assemblyControl && assemblyControl.value === 'service';
    const assemblyVariantId = assemblyControl ? assemblyControl.getAttribute('data-variant-id') : null;
    const hasAssemblyProduct = assemblyControl?.hasAttribute('data-variant-id') || false;
    if (wantsAssembly && hasAssemblyProduct && !Number.isFinite(Number.parseInt(assemblyVariantId, 10))) {
      alert('The assembly service product is unavailable right now.');
      return;
    }
    const cartItems = selectedParts
      .filter(part => part.selection && !part.selection.isPlaceholder)
      .map(part => ({
        partType: part.type,
        item: {
          id: Number.parseInt(part.selection.variantId, 10),
          quantity: 1,
          properties: {
            "Board Customizer Part": part.type
          }
        }
      }))
      .filter(entry => Number.isFinite(entry.item.id));
    if (wantsAssembly && assemblyVariantId) {
      cartItems.push({
        partType: 'Assembly',
        item: {
          id: Number.parseInt(assemblyVariantId, 10),
          quantity: 1
        }
      });
    }
    if (cartItems.length === 0) {
      alert('Choose at least one product part before adding to cart.');
      return;
    }
    const addToCartBtn = this.querySelector('#bc-add-to-cart-btn');
    const addToCartLabel = addToCartBtn?.querySelector('[data-add-to-cart-label]');
    if (!addToCartBtn || !addToCartLabel) return;
    const originalText = addToCartLabel.textContent;
    let addSucceeded = false;
    this.isAddingToCart = true;
    addToCartBtn.classList.remove('is-added');
    addToCartLabel.textContent = 'CHECKING...';
    addToCartBtn.disabled = true;
    try {
      try {
        const currentCart = await this.fetchCart();
        const replacementLines = this.getReplacementCartLines(currentCart, cartItems);
        if (replacementLines.length > 0) {
          const names = replacementLines
            .map(item => item.product_title || item.title || 'Selected part')
            .filter(Boolean)
            .slice(0, 4);
          const extraCount = Math.max(0, replacementLines.length - names.length);
          const nameList = names.join('\n- ');
          const extraLine = extraCount ? `\n- ${extraCount} more item${extraCount === 1 ? '' : 's'}` : '';
          const shouldReplace = window.confirm(
            `These selected parts are already in your cart:\n- ${nameList}${extraLine}\n\nThis can block checkout when a part only has 1 in stock. Replace those matching cart copies before adding this build? Everything else in your cart stays.`
          );
          if (!shouldReplace) return;
          addToCartLabel.textContent = 'REPLACING...';
          await this.removeCartLinesByKey(replacementLines.map(item => item.key).filter(Boolean));
        }
      } catch (cartCheckError) {
        console.warn('Could not check existing cart before adding build:', cartCheckError);
      }
      addToCartLabel.textContent = 'ADDING...';
      const payloadItems = cartItems.map(entry => entry.item);
      const response = await fetch(this.getCartEndpoint('/add.js'), {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items: payloadItems })
      });
      if (!response.ok) {
        throw new Error(await this.readCartAddError(response));
      }
      await response.json();
      addSucceeded = true;
      addToCartBtn.classList.add('is-added');
      addToCartLabel.textContent = 'ADDED';
      await this.openCartDrawer();
    } catch (err) {
      console.error('Could not add custom board to cart:', err, cartItems);
      alert(`Could not add your custom board to cart. ${err.message || 'Please try again.'}`);
    } finally {
      window.setTimeout(() => {
        addToCartLabel.textContent = originalText;
        addToCartBtn.disabled = false;
        addToCartBtn.classList.remove('is-added');
        this.isAddingToCart = false;
      }, addSucceeded ? 900 : 0);
    }
  }
}
if (!customElements.get('board-customizer')) {
  customElements.define('board-customizer', BoardCustomizer);
}
